// // before automatic  sms & email & business hours

// src/components/admin/LeadManagement.js
import axios from "axios";
import {
  AlarmClock,
  AlertCircle,
  Building,
  Calendar,
  Calendar as CalendarIcon,
  CheckCircle,
  Clipboard,
  Clock,
  Download,
  Edit,
  ExternalLink,
  Eye,
  Info,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  Settings,
  Trash2,
  User,
  UserPlus,
  X as XIcon
} from "lucide-react";
import { useEffect, useState } from "react";
import { useConfig } from "../../context/ConfigContext";
import { useNotification } from "../../context/NotificationContext";

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [notification, setNotification] = useState(null);
  const { updateLeadCount } = useNotification();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const { config } = useConfig();

  //follow up states for new leads
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [smsTemplate, setSmsTemplate] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);

  //appointmenr states
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointment, setAppointment] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(new Date());
  const [appointmentTime, setAppointmentTime] = useState("10:00");
  const [appointmentNotes, setAppointmentNotes] = useState("");
  const [appointmentStep, setAppointmentStep] = useState(1); // 1 = select date, 2 = message
  const [appointmentEmailTemplate, setAppointmentEmailTemplate] = useState("");
  const [appointmentSmsTemplate, setAppointmentSmsTemplate] = useState("");
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderType, setReminderType] = useState("manual"); // manual, schedule

  // q/survey state variables
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [questions, setQuestions] = useState([
    { id: 1, text: "What is your budget range?", answer: "" },
    { id: 2, text: "What is your timeline for this project?", answer: "" },
    { id: 3, text: "How did you hear about us?", answer: "" },
  ]);
  const [newQuestion, setNewQuestion] = useState("");
  const [isEditingQuestion, setIsEditingQuestion] = useState(null);

  const [showDetails, setShowDetails] = useState(false);

  // Lead Configuration States
const [showConfigModal, setShowConfigModal] = useState(false);
const [leadConfig, setLeadConfig] = useState({
  autoResponse: {
    enabled: false,
    businessHours: {
      weekdays: {
        enabled: true,
        start: '09:00',
        end: '17:00'
      },
      weekends: {
        enabled: false,
        start: '10:00',
        end: '15:00'
      },
      timezone: 'America/Toronto'
    },
    templates: {
      businessHours: {
        email: {
          subject: 'Thank you for your interest in {{siteName}}',
          body: `Dear {{customerName}},

Thank you for your interest in {{siteName}}! We've received your inquiry and one of our team members will get back to you within the next few hours.

In the meantime, feel free to browse our website or contact us directly at {{contactPhone}}.

Best regards,
The {{siteName}} Team
{{contactEmail}}
{{contactAddress}}`
        },
        sms: {
          body: 'Hi {{customerName}}! Thanks for your interest in {{siteName}}. We\'ll contact you shortly during business hours. Need immediate help? Call {{contactPhone}}'
        }
      },
      afterHours: {
        email: {
          subject: 'We\'ll be in touch soon - {{siteName}}',
          body: `Dear {{customerName}},

Thank you for your interest in {{siteName}}! We've received your inquiry after business hours.

Our business hours are:
Monday-Friday: {{weekdayHours}}
{{weekendHours}}

One of our team members will contact you during our next business day.

For urgent matters, please call {{contactPhone}}.

Best regards,
The {{siteName}} Team
{{contactEmail}}
{{contactAddress}}`
        },
        sms: {
          body: 'Hi {{customerName}}! Thanks for contacting {{siteName}}. We\'re currently closed but will respond during business hours. Urgent? Call {{contactPhone}}'
        }
      }
    }
  }
});
const [savingConfig, setSavingConfig] = useState(false);

  // Fetch leads from the API
  const fetchLeads = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = {
        page,
        search: searchTerm,
        status: statusFilter,
        source: sourceFilter,
      };

      const response = await axios.get("/api/admin/leads", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      console.log(config);
      setLeads(response.data.leads);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.page);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leads:", error);
      setNotification({
        type: "error",
        message: "Failed to load leads. Please try again.",
      });
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLeads();
      fetchLeadConfig();

  }, []);

  // Filter leads when search or filters change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchLeads(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, statusFilter, sourceFilter]);

  useEffect(() => {
    if (!loading && leads.length >= 0) {
      const newLeadsCount = leads.filter(
        (lead) => lead.status === "new"
      ).length;
      updateLeadCount(newLeadsCount);
    }
  }, [leads.length]); // Only depend on the length, not the entire array

  useEffect(() => {
    if (!showStatusDropdown) return;

    // Function to close dropdown when clicking outside
    const handleClickOutside = (event) => {
      // Check if click target is not part of the dropdown
      const dropdownElement = document.getElementById(
        "status-dropdown-container"
      );
      if (dropdownElement && !dropdownElement.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    // Add event listener to document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusDropdown]);

  useEffect(() => {
    if (!showStatusDropdown) return;

    // Function to close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const dropdownContainer = document.getElementById(
        `status-dropdown-${showStatusDropdown}`
      );
      if (dropdownContainer && !dropdownContainer.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    // Add event listener to document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusDropdown]);

  // View lead details
  // const viewLeadDetails = (lead) => {
  //   setSelectedLead(lead);
  //   setIsEditing(false);
  // };

  const viewLeadDetails = (lead) => {
    setSelectedLead(lead);
    setIsEditing(false);
    setShowDetails(true); // Show details panel when a lead is selected
  };

  // Add a function to close the details panel
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedLead(null);
    setIsEditing(false);
  };

  // Edit lead
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Update lead
    const handleUpdateLead = async () => {
    try {
      const token = localStorage.getItem("token");
      let response;

      if (selectedLead._id) {
        // Update existing lead
        response = await axios.put(
          `/api/admin/leads/${selectedLead._id}`,
          selectedLead,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setNotification({
          type: "success",
          message: "Lead updated successfully",
        });
      } else {
        // Create new lead
        response = await axios.post(`/api/admin/leads`, selectedLead, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setNotification({
          type: "success",
          message: "Lead created successfully",
        });
      }

      setIsEditing(false);
      fetchLeads(currentPage);
    } catch (error) {
      console.error("Error saving lead:", error);
      setNotification({
        type: "error",
        message: selectedLead._id
          ? "Failed to update lead"
          : "Failed to create lead",
      });
    }
  };

  // Handle input changes for editing
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedLead({
      ...selectedLead,
      [name]: value,
    });
  };

  const generateMessageTemplates = (lead) => {
    const name = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
    const user = JSON.parse(localStorage.getItem("user")) || {};
    // const adminName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    const adminName = `${user.firstName || ""}`.trim();

    // Email template
    const emailBody = `
  Dear ${name},
  
  Thank you for your interest in ${config.siteName} products/services. I'm reaching out regarding your recent inquiry.
  
  I'd love to learn more about your needs and how we can assist you. Would you be available for a brief call in the coming days?
  
  Please let me know what works best for you, or feel free to reach me directly at ${config.contactPhone}.
  
  Best regards,
  ${adminName}
  ${config.siteName}
    `;

    // SMS template
    const smsBody = `Hello ${name}, this is ${adminName} from ${config.siteName}. Thanks for your interest! I'd like to discuss how we can help you. When would be a good time to talk? Reply to this message or call ${config.contactPhone}.`;

    return { emailBody, smsBody };
  };

  // Add this function to handle sending email
  const handleSendEmail = async () => {
    try {
      setSendingMessage(true);
      const token = localStorage.getItem("token");

      await axios.post(
        "/api/admin/leads/send-email",
        {
          leadId: selectedLead._id,
          subject: `Following up on your interest in ${config.siteName} Products`,
          message: emailTemplate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (selectedLead.status === "new") {
        // Update lead status to contacted
        await axios.put(
          `/api/admin/leads/${selectedLead._id}`,
          { ...selectedLead, status: "contacted" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setSendingMessage(false);
      setMessageSuccess(true);

      // Close modal and refresh after 2 seconds
      setTimeout(() => {
        setShowEmailModal(false);
        setMessageSuccess(false);
        fetchLeads(currentPage);
      }, 2000);
    } catch (error) {
      console.error("Error sending email:", error);
      setSendingMessage(false);
      setNotification({
        type: "error",
        message: "Failed to send email",
      });
    }
  };

  // Add this function to handle sending SMS
  const handleSendSms = async () => {
    try {
      setSendingMessage(true);
      const token = localStorage.getItem("token");

      await axios.post(
        "/api/admin/leads/send-sms",
        {
          leadId: selectedLead._id,
          message: smsTemplate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (selectedLead.status === "new") {
        // Update lead status to contacted
        await axios.put(
          `/api/admin/leads/${selectedLead._id}`,
          { ...selectedLead, status: "contacted" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setSendingMessage(false);
      setMessageSuccess(true);

      // Close modal and refresh after 2 seconds
      setTimeout(() => {
        setShowSmsModal(false);
        setMessageSuccess(false);
        fetchLeads(currentPage);
      }, 2000);
    } catch (error) {
      console.error("Error sending SMS:", error);
      setSendingMessage(false);
      setNotification({
        type: "error",
        message: "Failed to send SMS",
      });
    }
  };

  const makeContact = (selectedLead) => {
    const token = localStorage.getItem("token");

    if (selectedLead.status === "new") {
      // Update lead status to contacted
      axios.put(
        `/api/admin/leads/${selectedLead._id}`,
        { ...selectedLead, status: "contacted" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Close modal and refresh after 2 seconds
    }
    // setTimeout(() => {
    //   fetchLeads(currentPage);
    //   closeDetails();
    // }, 6000);
  };

  const handleOpenAppointment = () => {
    setAppointmentDate(new Date());
    setAppointmentTime("10:00");
    setAppointmentNotes("");
    setAppointmentStep(1);
    setShowAppointmentModal(true);
  };

  const generateAppointmentTemplates = () => {
    const name = `${selectedLead.firstName || ""} ${selectedLead.lastName || ""
      }`.trim();
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const adminName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    const formattedDate = appointmentDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Email template
    const emailBody = `
Dear ${name},

I'm pleased to confirm your appointment with ${config.siteName
      } on ${formattedDate} at ${appointmentTime}.

Meeting details:
- Date: ${formattedDate}
- Time: ${appointmentTime}
${appointmentNotes ? `- Notes: ${appointmentNotes}` : ""}

If you need to reschedule or have any questions, please don't hesitate to contact us at ${config.contactPhone
      }.

We look forward to speaking with you!

Best regards,
${adminName}
${config.siteName}
${config.contactAddress}
  `;

    // SMS template
    const smsBody = `Hello ${name}, this is ${adminName} from ${config.siteName}. Your appointment is confirmed for ${formattedDate} at ${appointmentTime}. Please let us know if you need to reschedule. Looking forward to our meeting!`;

    return { emailBody, smsBody };
  };

  const handleStatusChange = async (newStatus, leadObj = null) => {
    try {
      const token = localStorage.getItem("token");

      // Determine which lead to update - either the explicitly passed lead (from list view)
      // or the currently selectedLead (from detail view)
      const leadToUpdate = leadObj || selectedLead;

      if (!leadToUpdate || !leadToUpdate._id) {
        console.error("No lead ID available for status update");
        return;
      }

      const updatedLead = { ...leadToUpdate, status: newStatus };

      await axios.put(`/api/admin/leads/${leadToUpdate._id}`, updatedLead, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // If we're updating the selectedLead, update it in state too
      if (!leadObj) {
        setSelectedLead(updatedLead);
      }

      setShowStatusDropdown(false);
      setNotification({
        type: "success",
        message: "Lead status updated successfully",
      });

      // Refresh leads list to reflect the change
      fetchLeads(currentPage);
    } catch (error) {
      console.error("Error updating lead status:", error);
      setNotification({
        type: "error",
        message: "Failed to update lead status",
      });
    }
  };

  const handleAppointmentNext = () => {
    const { emailBody, smsBody } = generateAppointmentTemplates();
    setAppointmentEmailTemplate(emailBody);
    setAppointmentSmsTemplate(smsBody);
    setAppointmentStep(2);
  };

  const handleScheduleAppointment = async (sendType) => {
    try {
      setSendingMessage(true);
      const token = localStorage.getItem("token");

      // First save the appointment data
      const appointmentData = {
        leadId: selectedLead._id,
        date: appointmentDate,
        time: appointmentTime,
        notes: appointmentNotes,
        status: "scheduled",
      };

      const response = await axios.post(
        "/api/admin/leads/appointments",
        appointmentData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const appointmentId = response.data._id;

      // Then send notification
      if (sendType === "email" && selectedLead.email) {
        await axios.post(
          "/api/admin/leads/send-email",
          {
            leadId: selectedLead._id,
            appointmentId,
            subject: `Your Appointment Confirmation - ${config.siteName}`,
            message: appointmentEmailTemplate,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else if (sendType === "sms" && selectedLead.phone) {
        await axios.post(
          "/api/admin/leads/send-sms",
          {
            leadId: selectedLead._id,
            appointmentId,
            message: appointmentSmsTemplate,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      // Update lead status to qualified
      await axios.put(
        `/api/admin/leads/${selectedLead._id}`,
        { ...selectedLead, status: "qualified" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSendingMessage(false);
      setMessageSuccess(true);

      // Close modal and refresh after 2 seconds
      setTimeout(() => {
        setShowAppointmentModal(false);
        setMessageSuccess(false);
        fetchLeads(currentPage);
      }, 2000);
    } catch (error) {
      console.error("Error scheduling appointment:", error);
      setSendingMessage(false);
      setNotification({
        type: "error",
        message: "Failed to schedule appointment",
      });
    }
  };

  const handleSetReminders = async () => {
    try {
      const token = localStorage.getItem("token");

      await axios.post(
        "/api/admin/leads/appointment-reminders",
        {
          leadId: selectedLead._id,
          appointmentId: selectedLead.appointment._id,
          reminderType,
          reminderTimes:
            reminderType === "schedule"
              ? [
                { hours: 72 }, // 3 days before
                { hours: 24 }, // 1 day before
                { hours: 1 }, // 1 hour before
              ]
              : [{ hours: 0 }], // Immediate
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setNotification({
        type: "success",
        message:
          reminderType === "schedule"
            ? "Reminders scheduled successfully"
            : "Reminder sent successfully",
      });

      setAppointment(true);
      setShowReminderModal(false);
    } catch (error) {
      console.error("Error setting reminders:", error);
      setNotification({
        type: "error",
        message: "Failed to set reminders",
      });
    }
  };

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return;

    const newId = Math.max(0, ...questions.map((q) => q.id)) + 1;
    setQuestions([...questions, { id: newId, text: newQuestion, answer: "" }]);
    setNewQuestion("");
  };

  const handleEditQuestion = (id, newText) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, text: newText } : q))
    );
  };

  const handleDeleteQuestion = (id) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const handleAnswerChange = (id, answer) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, answer } : q)));
  };

  const handleSaveSurvey = async () => {
    try {
      const token = localStorage.getItem("token");

      // Filter out unanswered questions
      const answeredQuestions = questions.filter((q) => q.answer.trim());

      if (answeredQuestions.length === 0) {
        setNotification({
          type: "error",
          message: "Please answer at least one question",
        });
        return;
      }

      await axios.post(
        "/api/admin/leads/survey",
        {
          leadId: selectedLead._id,
          questions: answeredQuestions,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setNotification({
        type: "success",
        message: "Survey saved successfully",
      });

      setShowSurveyModal(false);
      fetchLeads(currentPage);
    } catch (error) {
      console.error("Error saving survey:", error);
      setNotification({
        type: "error",
        message: "Failed to save survey",
      });
    }
  };

//   // Fetch lead configuration
const fetchLeadConfig = async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get("/api/admin/leads/config", {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.data) {
      // Ensure all template fields have values or set to defaults
      const configData = {
        ...response.data,
        autoResponse: {
          enabled: response.data.autoResponse?.enabled || false,
          businessHours: {
            weekdays: {
              enabled: response.data.autoResponse?.businessHours?.weekdays?.enabled ?? true,
              start: response.data.autoResponse?.businessHours?.weekdays?.start || '09:00',
              end: response.data.autoResponse?.businessHours?.weekdays?.end || '17:00'
            },
            weekends: {
              enabled: response.data.autoResponse?.businessHours?.weekends?.enabled ?? false,
              start: response.data.autoResponse?.businessHours?.weekends?.start || '10:00',
              end: response.data.autoResponse?.businessHours?.weekends?.end || '15:00'
            },
            timezone: response.data.autoResponse?.businessHours?.timezone || 'America/Toronto'
          },
          templates: {
            businessHours: {
              email: {
                subject: response.data.autoResponse?.templates?.businessHours?.email?.subject || 
                         getDefaultValueForPath('autoResponse.templates.businessHours.email.subject'),
                body: response.data.autoResponse?.templates?.businessHours?.email?.body || 
                      getDefaultValueForPath('autoResponse.templates.businessHours.email.body')
              },
              sms: {
                body: response.data.autoResponse?.templates?.businessHours?.sms?.body || 
                      getDefaultValueForPath('autoResponse.templates.businessHours.sms.body')
              }
            },
            afterHours: {
              email: {
                subject: response.data.autoResponse?.templates?.afterHours?.email?.subject || 
                         getDefaultValueForPath('autoResponse.templates.afterHours.email.subject'),
                body: response.data.autoResponse?.templates?.afterHours?.email?.body || 
                      getDefaultValueForPath('autoResponse.templates.afterHours.email.body')
              },
              sms: {
                body: response.data.autoResponse?.templates?.afterHours?.sms?.body || 
                      getDefaultValueForPath('autoResponse.templates.afterHours.sms.body')
              }
            }
          }
        }
      };
      
      setLeadConfig(configData);
    }
  } catch (error) {
    console.error("Error fetching lead configuration:", error);
    setNotification({
      type: "error",
      message: "Failed to load lead configuration.",
    });
  }
};

// const fetchLeadConfig = async () => {
//   try {
//     const token = localStorage.getItem("token");
//     const response = await axios.get("/api/admin/leads/config", {
//       headers: { Authorization: `Bearer ${token}` },
//     });

//     if (response.data) {
//       setLeadConfig(response.data);
//     }
//   } catch (error) {
//     console.error("Error fetching lead configuration:", error);
//     setNotification({
//       type: "error",
//       message: "Failed to load lead configuration.",
//     });
//   }
// };

// Save lead configuration
const saveLeadConfig = async () => {
  try {
    setSavingConfig(true);
    const token = localStorage.getItem("token");
    
    await axios.post("/api/admin/leads/config", leadConfig, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setNotification({
      type: "success",
      message: "Lead configuration saved successfully!",
    });

    setTimeout(() => {
      setNotification(null);
      setShowConfigModal(false);
    }, 2000);
  } catch (error) {
    console.error("Error saving lead configuration:", error);
    setNotification({
      type: "error",
      message: "Failed to save lead configuration.",
    });
  } finally {
    setSavingConfig(false);
  }
};

// Handle lead config field changes
const handleLeadConfigChange = (fieldPath, value) => {
  setLeadConfig((prev) => {
    const newConfig = { ...prev };
    const pathParts = fieldPath.split('.');
    let current = newConfig;

    // Navigate to the parent object
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }

    // Set the final value
    current[pathParts[pathParts.length - 1]] = value;
    return newConfig;
  });
};

// Helper function to get nested values
const getLeadConfigValue = (fieldPath, defaultValue = '') => {
  const pathParts = fieldPath.split('.');
  let current = leadConfig;

  for (const part of pathParts) {
    if (!current || current[part] === undefined) {
      return getDefaultValueForPath(fieldPath, defaultValue);
    }
    current = current[part];
  }

  // Check if the final value is empty/null and return default if so
  if (!current || current === '' || current === null) {
    return getDefaultValueForPath(fieldPath, defaultValue);
  }

  return current;
};



// Helper function to get default values
const getDefaultValueForPath = (fieldPath, fallbackDefault = '') => {
  const defaults = {
    'autoResponse.templates.businessHours.email.subject': 'Thank you for your interest in {{siteName}}',
    'autoResponse.templates.businessHours.email.body': `Dear {{customerName}},

Thank you for your interest in {{siteName}}! We've received your inquiry and one of our team members will get back to you within the next few hours.

In the meantime, feel free to browse our website or contact us directly at {{contactPhone}}.

Best regards,
The {{siteName}} Team
{{contactEmail}}
{{contactAddress}}`,
    'autoResponse.templates.businessHours.sms.body': 'Hi {{customerName}}! Thanks for your interest in {{siteName}}. We\'ll contact you shortly during business hours. Need immediate help? Call {{contactPhone}}',
    'autoResponse.templates.afterHours.email.subject': 'We\'ll be in touch soon - {{siteName}}',
    'autoResponse.templates.afterHours.email.body': `Dear {{customerName}},

Thank you for your interest in {{siteName}}! We've received your inquiry after business hours.

Our business hours are:
Monday-Friday: {{weekdayHours}}
Saturday-Sunday: {{weekendHours}}

One of our team members will contact you during our next business day.

For urgent matters, please call {{contactPhone}}.

Best regards,
The {{siteName}} Team
{{contactEmail}}
{{contactAddress}}`,
    'autoResponse.templates.afterHours.sms.body': 'Hi {{customerName}}! Thanks for contacting {{siteName}}. We\'re currently closed but will respond during business hours. Urgent? Call {{contactPhone}}'
  };

  return defaults[fieldPath] || fallbackDefault;
};

// const getLeadConfigValue = (fieldPath, defaultValue = '') => {
//   const pathParts = fieldPath.split('.');
//   let current = leadConfig;

//   for (const part of pathParts) {
//     if (!current || current[part] === undefined) {
//       return defaultValue;
//     }
//     current = current[part];
//   }

//   return current;
// };

  // Export leads as CSV
  const exportLeads = () => {
    // Convert leads to CSV format
    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Phone",
      "Company",
      "Source",
      "Status",
      "Created Date",
    ];
    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(","));

    // Add data rows
    leads.forEach((lead) => {
      const row = [
        lead.firstName || "",
        lead.lastName || "",
        lead.email || "",
        lead.phone || "",
        lead.company || "",
        lead.source || "",
        lead.status || "",
        new Date(lead.createdAt).toLocaleDateString(),
      ];

      // Escape commas in fields
      const csvRow = row
        .map((field) => {
          // If field contains comma, wrap in quotes
          return typeof field === "string" && field.includes(",")
            ? `"${field}"`
            : field;
        })
        .join(",");

      csvRows.push(csvRow);
    });

    // Create and download CSV file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `leads-export-${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {notification && (
        <div
          className={`p-4 rounded-md ${notification.type === "success"
            ? "bg-green-50 text-green-700"
            : "bg-red-50 text-red-700"
            }`}
        >
          <div className="flex items-center">
            {notification.type === "success" ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <span>{notification.message}</span>
            <button
              className="ml-auto text-gray-500 hover:text-gray-700"
              onClick={() => setNotification(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header and filters */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <h2 className="text-2xl font-semibold mb-4 md:mb-0">Lead Management</h2>
        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
          <button
            onClick={() => {
              setSelectedLead({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                company: "",
                title: "",
                status: "new",
                source: "manual",
                notes: "",
              });
              setIsEditing(true);
              setShowDetails(true);
            }}
            className="flex items-center px-3 py-2 bg-blue-600 text-white border border-blue-700 rounded-md hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Add Lead
          </button>

          <button
  onClick={() => setShowConfigModal(true)}
  className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200"
>
  <Settings className="w-4 h-4 mr-1" />
  Settings
</button>
        </div>
      </div>

      {/* Filters section */}
      {!showDetails && (
        <div className="flex flex-col lg:flex-row gap-2 w-full lg:w-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pr-7 pl-2 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="disqualified">Disqualified</option>
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="pr-7 pl-2 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Sources</option>
              <option value="meta">Meta</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="website">Website</option>
              <option value="manual">Manual</option>
              <option value="other">Other</option>
            </select>

            <button
              onClick={exportLeads}
              className="flex font-normal items-center px-2 pr-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              <Download className="w-4 h-4 mr-1" />
              Export
            </button>

            <button
              onClick={() => fetchLeads(currentPage)}
              className="flex font-normal items-center px-2 pr-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="bg-white rounded-lg shadow-md">
        {/* Conditionally render either leads list or lead details based on state */}
        {!showDetails ? (
          <>
            {/* Leads list panel */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium">Leads</h3>
            </div>

            {loading ? (
              <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No leads found. Try adjusting your filters.</p>
              </div>
            ) : (
              <div>
                {/* Header row - desktop only */}
                <div className="hidden lg:grid lg:grid-cols-12 bg-gray-50 py-3 px-4 border-b border-gray-200">
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </div>
                  <div className="col-span-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </div>
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </div>
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </div>
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </div>
                  <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </div>
                </div>

                {/* Lead rows */}
                <div className="divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <div
                      key={lead._id}
                      className="py-4 px-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => viewLeadDetails(lead)}
                    >
                      <div className="lg:grid lg:grid-cols-12 lg:gap-4 lg:items-center">
                        {/* Name & Company */}
                        <div className="flex items-center mb-3 lg:mb-0 lg:col-span-2">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.firstName} {lead.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lead.company || "No company"}
                            </div>
                          </div>
                        </div>

                        {/* Contact Info */}
                        <div className="mb-3 lg:mb-0 lg:col-span-3">
                          <div className="text-sm text-gray-900 lg:mb-1">
                            <span className="lg:hidden text-xs text-gray-500 mr-1">
                              Email:
                            </span>
                            {lead.email ? (
                              <span className="truncate block">
                                {lead.email}
                              </span>
                            ) : (
                              "No email"
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            <span className="lg:hidden text-xs text-gray-500 mr-1">
                              Phone:
                            </span>
                            {lead.phone || "No phone"}
                          </div>
                        </div>

                        {/* Source */}
                        <div className="mb-3 lg:mb-0 lg:col-span-2">
                          <span className="lg:hidden text-xs text-gray-500 mr-1">
                            Source:
                          </span>
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {lead.source}
                          </span>
                        </div>

                        {/* Status */}
                        {/* <div className="mb-3 lg:mb-0 lg:col-span-2">
                      <span className="lg:hidden text-xs text-gray-500 mr-1">Status:</span>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        lead.status === 'new' ? 'bg-green-100 text-green-800' :
                        lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                        lead.status === 'qualified' ? 'bg-blue-100 text-blue-800' :
                        lead.status === 'converted' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {lead.status || 'New'}
                      </span>
                    </div> */}
                        <div className="mb-3 lg:mb-0 lg:col-span-2">
                          <span className="lg:hidden text-xs text-gray-500 mr-1">
                            Status:
                          </span>
                          <div
                            className="relative inline-block"
                            id={`status-dropdown-${lead._id}`}
                          >
                            <div
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                setSelectedLead(lead);
                                setShowStatusDropdown(lead._id);
                              }}
                              className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded border border-gray-200 inline-flex items-center"
                            >
                              <span
                                className={`w-2 h-2 rounded-full mr-2 ${lead.status === "new"
                                  ? "bg-green-500"
                                  : lead.status === "contacted"
                                    ? "bg-yellow-500"
                                    : lead.status === "qualified"
                                      ? "bg-blue-500"
                                      : lead.status === "converted"
                                        ? "bg-purple-500"
                                        : "bg-gray-500"
                                  }`}
                              ></span>
                              <span className="text-xs font-medium capitalize">
                                {lead.status || "New"}
                              </span>
                              <svg
                                className="h-3 w-3 ml-1 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>

                            {/* Status dropdown for this specific lead */}
                            {showStatusDropdown === lead._id && (
                              <div
                                className="absolute z-50 w-36 bg-white rounded-md shadow-lg py-1 mt-1 border border-gray-200 right-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange("new", lead);
                                  }}
                                  style={{ boxShadow: "none" }}
                                  className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "new" ? "bg-gray-50" : ""
                                    }`}
                                >
                                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                                  New
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange("contacted", lead);
                                  }}
                                  style={{ boxShadow: "none" }}
                                  className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "contacted"
                                    ? "bg-gray-50"
                                    : ""
                                    }`}
                                >
                                  <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-2"></span>
                                  Contacted
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange("qualified", lead);
                                  }}
                                  style={{ boxShadow: "none" }}
                                  className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "qualified"
                                    ? "bg-gray-50"
                                    : ""
                                    }`}
                                >
                                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                                  Qualified
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange("converted", lead);
                                  }}
                                  style={{ boxShadow: "none" }}
                                  className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "converted"
                                    ? "bg-gray-50"
                                    : ""
                                    }`}
                                >
                                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                                  Converted
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange("disqualified", lead);
                                  }}
                                  style={{ boxShadow: "none" }}
                                  className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "disqualified"
                                    ? "bg-gray-50"
                                    : ""
                                    }`}
                                >
                                  <span className="inline-block w-2 h-2 rounded-full bg-gray-500 mr-2"></span>
                                  Disqualified
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Created Date */}
                        <div className="mb-3 lg:mb-0 lg:col-span-1">
                          <span className="lg:hidden text-xs text-gray-500 mr-1">
                            Created:
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(lead.createdAt).toLocaleDateString()}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end sm:justify-end md:justify-end lg:justify-center lg:ml-8 xl:ml-14 lg:col-span-2 mr-6">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              viewLeadDetails(lead);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 "
                            style={{
                              boxShadow: "none",
                              border: "none",
                              transform: "none",
                            }}
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLead(lead);
                              setIsEditing(true);
                              setShowDetails(true);
                            }}
                            className="text-green-600 hover:text-green-900 p-1 "
                            style={{
                              boxShadow: "none",
                              border: "none",
                              transform: "none",
                            }}
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => fetchLeads(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1
                      ? "bg-gray-100 text-gray-400"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchLeads(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages
                      ? "bg-gray-100 text-gray-400"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * leads.length + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          currentPage * leads.length,
                          totalPages * leads.length
                        )}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {totalPages * leads.length}
                      </span>{" "}
                      results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => fetchLeads(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${currentPage === 1
                          ? "bg-gray-100 text-gray-400"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <span className="sr-only">Previous</span>
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {/* Page numbers */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => fetchLeads(page)}
                            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${page === currentPage
                              ? "bg-blue-50 text-blue-600 border-blue-300 z-10"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                          >
                            {page}
                          </button>
                        )
                      )}

                      <button
                        onClick={() => fetchLeads(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${currentPage === totalPages
                          ? "bg-gray-100 text-gray-400"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <span className="sr-only">Next</span>
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Lead details panel */
          <div>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {!selectedLead
                  ? "Select a Lead"
                  : selectedLead._id
                    ? isEditing
                      ? "Edit Lead"
                      : "Lead Details"
                    : "Add New Lead"}
              </h3>
              <div className="flex space-x-2">
                {selectedLead && !isEditing && (
                  <button
                    onClick={handleEditToggle}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </button>
                )}
                <button
                  onClick={closeDetails}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100"
                >
                  <XIcon className="w-4 h-4 mr-1" />
                  Close
                </button>
              </div>
            </div>

            <div className="p-4">
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={selectedLead.firstName || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={selectedLead.lastName || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={selectedLead.email || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Phone
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={selectedLead.phone || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={selectedLead.company || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={selectedLead.title || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <select
                        name="status"
                        value={selectedLead.status || "new"}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="converted">Converted</option>
                        <option value="disqualified">Disqualified</option>
                      </select>
                    </div>

                    {/* new field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Source
                      </label>
                      <select
                        name="source"
                        value={selectedLead.source || "manual"}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="manual">Manual Entry</option>
                        <option value="meta">Meta</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="website">Website</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        rows="4"
                        value={selectedLead.notes || ""}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      ></textarea>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        if (!selectedLead._id) {
                          // Clear form if this was a new lead
                          setSelectedLead(null);
                          setShowDetails(false);
                        }
                      }}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleUpdateLead();
                        // After saving, you might want to show the updated lead details
                        setTimeout(() => {
                          setIsEditing(false);
                          fetchLeads(currentPage);
                        }, 300);
                      }}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      {selectedLead._id ? "Save Changes" : "Create Lead"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="block lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:gap-6">
                    {/* Name field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Name
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        {selectedLead.firstName} {selectedLead.lastName}
                      </div>
                    </div>

                    {/* Email field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Email
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        {selectedLead.email ? (
                          <a
                            href={`mailto:${selectedLead.email}`}
                            className="flex items-center text-blue-600 hover:underline"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            {selectedLead.email}
                          </a>
                        ) : (
                          "No email provided"
                        )}
                      </div>
                    </div>

                    {/* Phone field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Phone
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        {selectedLead.phone ? (
                          <a
                            href={`tel:${selectedLead.phone}`}
                            className="flex items-center text-blue-600 hover:underline"
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            {selectedLead.phone}
                          </a>
                        ) : (
                          "No phone provided"
                        )}
                      </div>
                    </div>

                    {/* Company field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Company
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        {selectedLead.company ? (
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-1" />
                            {selectedLead.company}
                          </div>
                        ) : (
                          "No company provided"
                        )}
                      </div>
                    </div>

                    {/* Source field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Source
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {selectedLead.source}
                        </span>
                      </div>
                    </div>

                    {/* Status field */}
                    {/* <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                    <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">Status</div>
                    <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        selectedLead.status === 'new' ? 'bg-green-100 text-green-800' :
                        selectedLead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                        selectedLead.status === 'qualified' ? 'bg-blue-100 text-blue-800' :
                        selectedLead.status === 'converted' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedLead.status || 'New'}
                      </span>
                    </div>
                  </div> */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-visible">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Status
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        <div
                          className="relative inline-block w-full"
                          id="status-dropdown-container"
                        >
                          <div
                            onClick={() =>
                              !isEditing &&
                              setShowStatusDropdown(!showStatusDropdown)
                            }
                            className={`inline-flex items-center justify-between ${isEditing
                              ? "cursor-not-allowed opacity-70"
                              : "cursor-pointer hover:bg-gray-50"
                              } rounded px-2 py-1 w-40 border border-gray-200`}
                          >
                            <div className="flex items-center">
                              <span
                                className={`w-3 h-3 rounded-full mr-2 ${selectedLead.status === "new"
                                  ? "bg-green-500"
                                  : selectedLead.status === "contacted"
                                    ? "bg-yellow-500"
                                    : selectedLead.status === "qualified"
                                      ? "bg-blue-500"
                                      : selectedLead.status === "converted"
                                        ? "bg-purple-500"
                                        : "bg-gray-500"
                                  }`}
                              ></span>
                              <span className="text-sm font-medium capitalize">
                                {selectedLead.status || "New"}
                              </span>
                            </div>
                            {!isEditing && (
                              <svg
                                className="h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>

                          {/* Status dropdown - improved version */}
                          {showStatusDropdown && !isEditing && (
                            <div
                              className="absolute z-50 w-40 bg-white rounded-md shadow-lg py-1 mt-1 border border-gray-200 left-0"
                              style={{ position: "absolute", top: "100%" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => handleStatusChange("new")}
                                style={{ boxShadow: "none" }}
                                className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "new"
                                  ? "bg-gray-50"
                                  : ""
                                  }`}
                              >
                                <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                                New
                              </button>
                              <button
                                onClick={() => handleStatusChange("contacted")}
                                style={{ boxShadow: "none" }}
                                className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "contacted"
                                  ? "bg-gray-50"
                                  : ""
                                  }`}
                              >
                                <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>
                                Contacted
                              </button>
                              <button
                                onClick={() => handleStatusChange("qualified")}
                                style={{ boxShadow: "none" }}
                                className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "qualified"
                                  ? "bg-gray-50"
                                  : ""
                                  }`}
                              >
                                <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                                Qualified
                              </button>
                              <button
                                onClick={() => handleStatusChange("converted")}
                                style={{ boxShadow: "none" }}
                                className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "converted"
                                  ? "bg-gray-50"
                                  : ""
                                  }`}
                              >
                                <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-2"></span>
                                Converted
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusChange("disqualified")
                                }
                                style={{ boxShadow: "none" }}
                                className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "disqualified"
                                  ? "bg-gray-50"
                                  : ""
                                  }`}
                              >
                                <span className="inline-block w-3 h-3 rounded-full bg-gray-500 mr-2"></span>
                                Disqualified
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Title field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Title
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
                        {selectedLead.title || "N/A"}
                      </div>
                    </div>

                    {/* Created field */}
                    <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Created
                      </div>
                      <div className="text-sm text-gray-900 lg:px-4 lg:py-3 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(selectedLead.createdAt).toLocaleDateString()}
                        <Clock className="w-4 h-4 ml-2 mr-1" />
                        {new Date(selectedLead.createdAt).toLocaleTimeString()}
                      </div>
                    </div>

                    {/* Source ID field (conditional) */}
                    {selectedLead.sourceId && (
                      <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
                        <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                          Source ID
                        </div>
                        <div className="text-sm text-gray-900 lg:px-4 lg:py-3 flex items-center">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {selectedLead.sourceId}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes section (kept separate since it's longer content) */}
                  {selectedLead.notes && (
                    <div className="pt-3 md:pt-3 lg:pt-0 pt-2 mt-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:overflow-hidden">
                      <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
                        Notes
                      </div>
                      <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm text-gray-700 lg:bg-white lg:mt-0">
                        {selectedLead.notes}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between pt-4">
                    {/* for new leads */}
                    {!isEditing && selectedLead && (
                      <div className="space-x-2">
                        {selectedLead.email && (
                          <button
                            onClick={() => {
                              const { emailBody } =
                                generateMessageTemplates(selectedLead);
                              setEmailTemplate(emailBody);
                              setShowEmailModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium border border-blue-300 rounded-md hover:bg-blue-200"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Send Email
                          </button>
                        )}
                        {selectedLead.phone && (
                          <button
                            onClick={() => {
                              const { smsBody } =
                                generateMessageTemplates(selectedLead);
                              setSmsTemplate(smsBody);
                              setShowSmsModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 text-sm font-medium rounded-md"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Send SMS
                          </button>
                        )}
                        {selectedLead.phone && (
                          <button
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
                            onClick={() => {
                              makeContact(selectedLead);
                            }}
                          >
                            <a
                              className="inline-flex items-center"
                              href={`tel:${selectedLead.phone}`}
                            >
                              <Phone className="w-4 h-4 mr-1" />
                              Call
                            </a>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between pt-4">
                    <div className="space-x-2">
                      {/*contacted leads */}
                      {!isEditing &&
                        selectedLead &&
                        selectedLead.status !== "new" && (
                          <button
                            onClick={handleOpenAppointment}
                            className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 border border-purple-300 rounded-md hover:bg-purple-200 text-sm font-medium"
                          >
                            <Calendar className="w-4 h-4 mr-1" />
                            <span className="hidden md:inline">Send&nbsp;</span>
                            Appointment
                          </button>
                        )}

                      {/* leads with appointments */}
                      {!isEditing &&
                        selectedLead &&
                        selectedLead.status !== "new" && (
                          <button
                            onClick={() => setShowReminderModal(true)}
                            className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 border border-yellow-300 rounded-md hover:bg-yellow-200 text-sm font-medium"
                          >
                            <AlarmClock className="w-4 h-4 mr-1" />
                            <span className="hidden md:inline">
                              Send&nbsp;
                            </span>{" "}
                            Reminder
                          </button>
                        )}

                      {/* contacted leads survey /q*/}
                      {!isEditing &&
                        selectedLead &&
                        selectedLead.status !== "new" && (
                          <button
                            onClick={() => setShowSurveyModal(true)}
                            className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-teal-100 text-teal-700 border border-teal-300 hover:bg-teal-200"
                          >
                            <Clipboard className="w-4 h-4 mr-1" />
                            Survey
                            <span className="hidden lg:inline">
                              &nbsp;Questions
                            </span>
                          </button>
                        )}
                    </div>
                  </div>

                  {/* display appointment details */}
                  {!isEditing &&
                    selectedLead &&
                    selectedLead.appointment &&
                    selectedLead.status !== "new" && (
                      <div className="space-x-2">
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-1 text-purple-600" />
                          Scheduled Appointment
                        </h4>
                        <div className="bg-purple-50 rounded-md p-3 text-sm">
                          <div className="flex items-center mb-1">
                            <Calendar className="w-4 h-4 mr-1 text-purple-600" />
                            <span>
                              {new Date(
                                selectedLead.appointment.date
                              ).toLocaleDateString("en-US", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="flex items-center mb-1">
                            <Clock className="w-4 h-4 mr-1 text-purple-600" />
                            <span>{selectedLead.appointment.time}</span>
                          </div>
                          {selectedLead.appointment.notes && (
                            <div className="mt-2 text-gray-700">
                              <p className="italic">
                                {selectedLead.appointment.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Add this to display survey data */}
                  {!isEditing &&
                    selectedLead &&
                    selectedLead.survey &&
                    selectedLead.survey.length > 0 && (
                      <div className="mt-4 border-t pt-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Survey Responses
                        </h4>
                        <div className="space-y-2">
                          {selectedLead.survey.map((item, index) => (
                            <div
                              key={index}
                              className="bg-teal-50 rounded-md p-3"
                            >
                              <p className="font-medium text-teal-800">
                                {item.text}
                              </p>
                              <p className="text-gray-700 mt-1">
                                {item.answer}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">
                Send Email to {selectedLead.firstName} {selectedLead.lastName}
              </h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            {messageSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <p className="text-lg font-medium">Email sent successfully!</p>
                <p className="text-gray-500 mt-1">
                  Lead status updated to "Contacted"
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To:
                  </label>
                  <div className="p-2 bg-gray-100 rounded text-sm">
                    {selectedLead.email}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject:
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    defaultValue={`Following up on your interest in ${config.siteName} products`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message:
                  </label>
                  <textarea
                    rows={10}
                    className="w-full p-2 border rounded"
                    value={emailTemplate}
                    onChange={(e) => setEmailTemplate(e.target.value)}
                  ></textarea>
                </div>
              </div>
            )}

            <div className="p-6 border-t flex justify-end space-x-3">
              {!messageSuccess && (
                <>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingMessage}
                    className={`px-4 py-2 rounded-md text-white ${sendingMessage
                      ? "bg-blue-400"
                      : "bg-blue-600 hover:bg-blue-700"
                      }`}
                  >
                    {sendingMessage ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      "Send Email"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">
                {appointmentStep === 1
                  ? "Schedule Appointment"
                  : "Confirm Appointment"}
              </h3>
              <button
                onClick={() => setShowAppointmentModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            {messageSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <p className="text-lg font-medium">Appointment scheduled!</p>
                <p className="text-gray-500 mt-1">
                  Lead status updated to "Qualified"
                </p>
              </div>
            ) : appointmentStep === 1 ? (
              <div className="flex-1 overflow-auto p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Date:
                  </label>
                  <input
                    type="date"
                    className="w-full p-2 border rounded"
                    value={appointmentDate.toISOString().split("T")[0]}
                    onChange={(e) =>
                      setAppointmentDate(new Date(e.target.value))
                    }
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Time:
                  </label>
                  <input
                    type="time"
                    className="w-full p-2 border rounded"
                    value={appointmentTime}
                    onChange={(e) => setAppointmentTime(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes:
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Enter any additional information about the appointment"
                    className="w-full p-2 border rounded"
                    value={appointmentNotes}
                    onChange={(e) => setAppointmentNotes(e.target.value)}
                  ></textarea>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-auto p-6">
                <div className="bg-gray-50 p-3 rounded-md mb-4">
                  <div className="flex items-center mb-1">
                    <Calendar className="w-4 h-4 mr-1 text-purple-600" />
                    <span>
                      {appointmentDate.toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1 text-purple-600" />
                    <span>{appointmentTime}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={selectedLead.email !== undefined}
                      readOnly
                      className={`h-4 w-4 ${!selectedLead.email ? "opacity-50" : ""
                        }`}
                    />
                    <span
                      className={`${!selectedLead.email ? "text-gray-400" : ""
                        }`}
                    >
                      Send Email {!selectedLead.email && "(No email address)"}
                    </span>
                  </label>

                  {selectedLead.email && (
                    <div className="mt-2 ml-6">
                      <textarea
                        rows={8}
                        className="w-full p-2 border rounded"
                        value={appointmentEmailTemplate}
                        onChange={(e) =>
                          setAppointmentEmailTemplate(e.target.value)
                        }
                      ></textarea>
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={selectedLead.phone !== undefined}
                      readOnly
                      className={`h-4 w-4 ${!selectedLead.phone ? "opacity-50" : ""
                        }`}
                    />
                    <span
                      className={`${!selectedLead.phone ? "text-gray-400" : ""
                        }`}
                    >
                      Send SMS {!selectedLead.phone && "(No phone number)"}
                    </span>
                  </label>

                  {selectedLead.phone && (
                    <div className="mt-2 ml-6">
                      <textarea
                        rows={4}
                        className="w-full p-2 border rounded"
                        value={appointmentSmsTemplate}
                        onChange={(e) =>
                          setAppointmentSmsTemplate(e.target.value)
                        }
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">
                        Characters: {appointmentSmsTemplate.length}/160
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="p-6 border-t flex justify-end space-x-3">
              {!messageSuccess && (
                <>
                  <button
                    onClick={() => {
                      if (appointmentStep === 2) {
                        setAppointmentStep(1);
                      } else {
                        setShowAppointmentModal(false);
                      }
                    }}
                    className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    {appointmentStep === 2 ? "Back" : "Cancel"}
                  </button>

                  {appointmentStep === 1 ? (
                    <button
                      onClick={handleAppointmentNext}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                      Next
                    </button>
                  ) : (
                    <>
                      {selectedLead.email && (
                        <button
                          onClick={() => handleScheduleAppointment("email")}
                          disabled={sendingMessage}
                          className={`px-4 py-2 rounded-md text-white ${sendingMessage
                            ? "bg-blue-400"
                            : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                          {sendingMessage ? "Sending..." : "Send Email"}
                        </button>
                      )}

                      {selectedLead.phone && (
                        <button
                          onClick={() => handleScheduleAppointment("sms")}
                          disabled={sendingMessage}
                          className={`px-4 py-2 rounded-md text-white ${sendingMessage
                            ? "bg-green-400"
                            : "bg-green-600 hover:bg-green-700"
                            }`}
                        >
                          {sendingMessage ? "Sending..." : "Send SMS"}
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">Send Appointment Reminder</h3>
              <button
                onClick={() => setShowReminderModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <div className="flex items-center mb-1">
                  <Calendar className="w-4 h-4 mr-1 text-purple-600" />
                  <span>
                    {new Date(selectedLead.appointment.date).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-purple-600" />
                  <span>{selectedLead.appointment.time}</span>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={reminderType === "manual"}
                    onChange={() => setReminderType("manual")}
                    className="h-4 w-4"
                  />
                  <span>Send reminder now</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={reminderType === "schedule"}
                    onChange={() => setReminderType("schedule")}
                    className="h-4 w-4"
                  />
                  <span>Schedule automatic reminders</span>
                </label>

                {reminderType === "schedule" && (
                  <div className="ml-6 bg-blue-50 p-3 rounded-md text-sm">
                    <p>Reminders will be sent:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>3 days before appointment</li>
                      <li>1 day before appointment</li>
                      <li>1 hour before appointment</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowReminderModal(false)}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSetReminders}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
              >
                {reminderType === "manual"
                  ? "Send Reminder"
                  : "Schedule Reminders"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Modal */}
      {showSmsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">
                Send SMS to {selectedLead.firstName} {selectedLead.lastName}
              </h3>
              <button
                onClick={() => setShowSmsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            {messageSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <p className="text-lg font-medium">SMS sent successfully!</p>
                <p className="text-gray-500 mt-1">
                  Lead status updated to "Contacted"
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To:
                  </label>
                  <div className="p-2 bg-gray-100 rounded text-sm">
                    {selectedLead.phone}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message:
                  </label>
                  <textarea
                    rows={6}
                    className="w-full p-2 border rounded"
                    value={smsTemplate}
                    onChange={(e) => setSmsTemplate(e.target.value)}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    Characters: {smsTemplate.length}/160
                    {smsTemplate.length > 160 && (
                      <span className="text-red-500">
                        {" "}
                        (Will be sent as multiple messages)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="p-6 border-t flex justify-end space-x-3">
              {!messageSuccess && (
                <>
                  <button
                    onClick={() => setShowSmsModal(false)}
                    className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendSms}
                    disabled={sendingMessage}
                    className={`px-4 py-2 rounded-md text-white ${sendingMessage
                      ? "bg-green-400"
                      : "bg-green-600 hover:bg-green-700"
                      }`}
                  >
                    {sendingMessage ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      "Send SMS"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Survey Modal */}
      {showSurveyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">
                Lead Survey: {selectedLead.firstName} {selectedLead.lastName}
              </h3>
              <button
                onClick={() => setShowSurveyModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {/* Question manager */}
              <div className="mb-6 pb-6 border-b">
                <h4 className="font-medium text-gray-900 mb-3">
                  Manage Questions
                </h4>

                <div className="mb-4 flex">
                  <input
                    type="text"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    placeholder="Add a new question..."
                    className="flex-1 p-2 border rounded-l"
                  />
                  <button
                    onClick={handleAddQuestion}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>

                {questions.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {questions.map((question) => (
                      <div
                        key={question.id}
                        className="flex items-center bg-gray-50 p-2 rounded"
                      >
                        {isEditingQuestion === question.id ? (
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) =>
                              handleEditQuestion(question.id, e.target.value)
                            }
                            className="flex-1 p-1 border rounded mr-2"
                            autoFocus
                            onBlur={() => setIsEditingQuestion(null)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") setIsEditingQuestion(null);
                            }}
                          />
                        ) : (
                          <span className="flex-1">{question.text}</span>
                        )}

                        <div className="flex space-x-1">
                          <button
                            onClick={() => setIsEditingQuestion(question.id)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Survey form */}
              <h4 className="font-medium text-gray-900 mb-3">
                Capture Responses
              </h4>

              {questions.length === 0 ? (
                <p className="text-gray-500 italic">
                  No questions available. Add questions above.
                </p>
              ) : (
                <div className="space-y-4">
                  {questions.map((question) => (
                    <div key={question.id} className="bg-gray-50 p-3 rounded">
                      <label className="block font-medium text-gray-700 mb-2">
                        {question.text}
                      </label>
                      <textarea
                        rows={3}
                        value={question.answer}
                        onChange={(e) =>
                          handleAnswerChange(question.id, e.target.value)
                        }
                        placeholder="Enter the customer's response..."
                        className="w-full p-2 border rounded"
                      ></textarea>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowSurveyModal(false)}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSurvey}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                Save Survey
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Configuration Modal */}
{showConfigModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
      <div className="flex justify-between items-center p-6 border-b">
        <h3 className="text-lg font-medium">Lead Configuration</h3>
        <button
          onClick={() => setShowConfigModal(false)}
          className="text-gray-400 hover:text-gray-500"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* Auto Response Toggle */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={getLeadConfigValue('autoResponse.enabled', false)}
                onChange={(e) => handleLeadConfigChange('autoResponse.enabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                Enable Automatic Lead Response
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Automatically send emails and SMS to new leads based on business hours
            </p>
          </div>

          {getLeadConfigValue('autoResponse.enabled') && (
            <>
              {/* Business Hours Configuration */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-blue-600" />
                  Business Hours Configuration
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Weekdays */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={getLeadConfigValue('autoResponse.businessHours.weekdays.enabled', true)}
                        onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.weekdays.enabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Weekdays (Monday - Friday)
                      </span>
                    </div>

                    {getLeadConfigValue('autoResponse.businessHours.weekdays.enabled') && (
                      <div className="ml-6 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={getLeadConfigValue('autoResponse.businessHours.weekdays.start', '09:00')}
                            onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.weekdays.start', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={getLeadConfigValue('autoResponse.businessHours.weekdays.end', '17:00')}
                            onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.weekdays.end', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Weekends */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={getLeadConfigValue('autoResponse.businessHours.weekends.enabled', false)}
                        onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.weekends.enabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Weekends (Saturday - Sunday)
                      </span>
                    </div>

                    {getLeadConfigValue('autoResponse.businessHours.weekends.enabled') && (
                      <div className="ml-6 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={getLeadConfigValue('autoResponse.businessHours.weekends.start', '10:00')}
                            onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.weekends.start', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={getLeadConfigValue('autoResponse.businessHours.weekends.end', '15:00')}
                            onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.weekends.end', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timezone */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    value={getLeadConfigValue('autoResponse.businessHours.timezone', 'America/Toronto')}
                    onChange={(e) => handleLeadConfigChange('autoResponse.businessHours.timezone', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="America/Toronto">Eastern Time (Toronto)</option>
                    <option value="America/Vancouver">Pacific Time (Vancouver)</option>
                    <option value="America/Edmonton">Mountain Time (Edmonton)</option>
                    <option value="America/Winnipeg">Central Time (Winnipeg)</option>
                    <option value="America/Halifax">Atlantic Time (Halifax)</option>
                    <option value="America/St_Johns">Newfoundland Time (St. Johns)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select your business timezone</p>
                </div>
              </div>

              {/* Business Hours Templates */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-green-600" />
                  During Business Hours Templates
                </h4>

                <div className="space-y-4">
                  {/* Email Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={getLeadConfigValue('autoResponse.templates.businessHours.email.subject', '')}
                      onChange={(e) => handleLeadConfigChange('autoResponse.templates.businessHours.email.subject', e.target.value)}
                      placeholder="Thank you for your interest in {{siteName}}"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Body
                    </label>
                    <textarea
                      rows={6}
                      value={getLeadConfigValue('autoResponse.templates.businessHours.email.body', '')}
                      onChange={(e) => handleLeadConfigChange('autoResponse.templates.businessHours.email.body', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      Available variables: {`{{customerName}}, {{siteName}}, {{contactPhone}}, {{contactEmail}}, {{contactAddress}}`}
                    </p>
                  </div>

                  {/* SMS Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SMS Message
                    </label>
                    <textarea
                      rows={3}
                      value={getLeadConfigValue('autoResponse.templates.businessHours.sms.body', '')}
                      onChange={(e) => handleLeadConfigChange('autoResponse.templates.businessHours.sms.body', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
Available variables: {'{{customerName}}, {{siteName}}, {{contactPhone}}'} Current length: {getLeadConfigValue('autoResponse.templates.businessHours.sms.body', '').length}/160 characters
                    </p>
                  </div>
                </div>
              </div>

              {/* After Hours Templates */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-orange-600" />
                  After Business Hours Templates
                </h4>

                <div className="space-y-4">
                  {/* Email Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={getLeadConfigValue('autoResponse.templates.afterHours.email.subject', '')}
                      onChange={(e) => handleLeadConfigChange('autoResponse.templates.afterHours.email.subject', e.target.value)}
                      placeholder="We'll be in touch soon - {{siteName}}"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Body
                    </label>
                    <textarea
                      rows={8}
                      value={getLeadConfigValue('autoResponse.templates.afterHours.email.body', '')}
                      onChange={(e) => handleLeadConfigChange('autoResponse.templates.afterHours.email.body', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      Available variables: {` {{customerName}}, {{siteName}}, {{contactPhone}}, {{contactEmail}}, {{contactAddress}}, {{weekdayHours}}, {{weekendHours}}`}
                    </p>
                  </div>

                  {/* SMS Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SMS Message
                    </label>
                    <textarea
                      rows={3}
                      value={getLeadConfigValue('autoResponse.templates.afterHours.sms.body', '')}
                      onChange={(e) => handleLeadConfigChange('autoResponse.templates.afterHours.sms.body', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
Available variables: {'{{customerName}}, {{siteName}}, {{contactPhone}}'} Current length: {getLeadConfigValue('autoResponse.templates.businessHours.sms.body', '').length}/160 characters
                    </p>
                  </div>
                </div>
              </div>

              {/* Template Variables Help */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Available Template Variables
                </h5>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><code className="bg-blue-100 px-1 rounded">{'{{customerName}}'}</code> - Lead's first and last name</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{siteName}}'}</code> - Your website name</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{contactPhone}}'}</code> - Your contact phone number</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{contactEmail}}'}</code> - Your contact email</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{contactAddress}}'}</code> - Your business address</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{weekdayHours}}'}</code> - Weekday business hours (after-hours only)</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{weekendHours}}'}</code> - Weekend business hours (after-hours only)</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="p-6 border-t flex justify-end space-x-3">
        <button
          onClick={() => setShowConfigModal(false)}
          className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={saveLeadConfig}
          disabled={savingConfig}
          className={`px-4 py-2 rounded-md text-white ${savingConfig
            ? "bg-blue-400"
            : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {savingConfig ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default LeadManagement;









// // before automatic  sms & email & business hours
// // src/components/admin/LeadManagement.js
// import axios from "axios";
// import {
//   AlarmClock,
//   AlertCircle,
//   Building,
//   Calendar,
//   Calendar as CalendarIcon,
//   CheckCircle,
//   Clipboard,
//   Clock,
//   Download,
//   Edit,
//   ExternalLink,
//   Eye,
//   Mail,
//   MessageSquare,
//   Phone,
//   RefreshCw,
//   Trash2,
//   User,
//   UserPlus,
//   X as XIcon
// } from "lucide-react";
// import { useEffect, useState } from "react";
// import { useConfig } from "../../context/ConfigContext";
// import { useNotification } from "../../context/NotificationContext";

// const LeadManagement = () => {
//   const [leads, setLeads] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [selectedLead, setSelectedLead] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");
//   const [sourceFilter, setSourceFilter] = useState("");
//   const [notification, setNotification] = useState(null);
//   const { updateLeadCount } = useNotification();
//   const [showStatusDropdown, setShowStatusDropdown] = useState(false);
//   const { config } = useConfig();

//   //follow up states for new leads
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [showSmsModal, setShowSmsModal] = useState(false);
//   const [emailTemplate, setEmailTemplate] = useState("");
//   const [smsTemplate, setSmsTemplate] = useState("");
//   const [sendingMessage, setSendingMessage] = useState(false);
//   const [messageSuccess, setMessageSuccess] = useState(false);

//   //appointmenr states
//   const [showAppointmentModal, setShowAppointmentModal] = useState(false);
//   const [appointment, setAppointment] = useState(false);
//   const [appointmentDate, setAppointmentDate] = useState(new Date());
//   const [appointmentTime, setAppointmentTime] = useState("10:00");
//   const [appointmentNotes, setAppointmentNotes] = useState("");
//   const [appointmentStep, setAppointmentStep] = useState(1); // 1 = select date, 2 = message
//   const [appointmentEmailTemplate, setAppointmentEmailTemplate] = useState("");
//   const [appointmentSmsTemplate, setAppointmentSmsTemplate] = useState("");
//   const [showReminderModal, setShowReminderModal] = useState(false);
//   const [reminderType, setReminderType] = useState("manual"); // manual, schedule

//   // q/survey state variables
//   const [showSurveyModal, setShowSurveyModal] = useState(false);
//   const [questions, setQuestions] = useState([
//     { id: 1, text: "What is your budget range?", answer: "" },
//     { id: 2, text: "What is your timeline for this project?", answer: "" },
//     { id: 3, text: "How did you hear about us?", answer: "" },
//   ]);
//   const [newQuestion, setNewQuestion] = useState("");
//   const [isEditingQuestion, setIsEditingQuestion] = useState(null);

//   const [showDetails, setShowDetails] = useState(false);

//   // Fetch leads from the API
//   const fetchLeads = async (page = 1) => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem("token");
//       const params = {
//         page,
//         search: searchTerm,
//         status: statusFilter,
//         source: sourceFilter,
//       };

//       const response = await axios.get("/api/admin/leads", {
//         headers: { Authorization: `Bearer ${token}` },
//         params,
//       });

//       console.log(config);
//       setLeads(response.data.leads);
//       setTotalPages(response.data.totalPages);
//       setCurrentPage(response.data.page);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching leads:", error);
//       setNotification({
//         type: "error",
//         message: "Failed to load leads. Please try again.",
//       });
//       setLoading(false);
//     }
//   };

//   // Initial fetch
//   useEffect(() => {
//     fetchLeads();
//   }, []);

//   // Filter leads when search or filters change
//   useEffect(() => {
//     const delayDebounceFn = setTimeout(() => {
//       fetchLeads(1);
//     }, 500);

//     return () => clearTimeout(delayDebounceFn);
//   }, [searchTerm, statusFilter, sourceFilter]);

//   useEffect(() => {
//     if (!loading && leads.length >= 0) {
//       const newLeadsCount = leads.filter(
//         (lead) => lead.status === "new"
//       ).length;
//       updateLeadCount(newLeadsCount);
//     }
//   }, [leads.length]); // Only depend on the length, not the entire array

//   useEffect(() => {
//     if (!showStatusDropdown) return;

//     // Function to close dropdown when clicking outside
//     const handleClickOutside = (event) => {
//       // Check if click target is not part of the dropdown
//       const dropdownElement = document.getElementById(
//         "status-dropdown-container"
//       );
//       if (dropdownElement && !dropdownElement.contains(event.target)) {
//         setShowStatusDropdown(false);
//       }
//     };

//     // Add event listener to document
//     document.addEventListener("mousedown", handleClickOutside);

//     // Clean up event listener
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [showStatusDropdown]);

//   useEffect(() => {
//     if (!showStatusDropdown) return;

//     // Function to close dropdown when clicking outside
//     const handleClickOutside = (event) => {
//       const dropdownContainer = document.getElementById(
//         `status-dropdown-${showStatusDropdown}`
//       );
//       if (dropdownContainer && !dropdownContainer.contains(event.target)) {
//         setShowStatusDropdown(false);
//       }
//     };

//     // Add event listener to document
//     document.addEventListener("mousedown", handleClickOutside);

//     // Clean up event listener
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [showStatusDropdown]);

//   // View lead details
//   // const viewLeadDetails = (lead) => {
//   //   setSelectedLead(lead);
//   //   setIsEditing(false);
//   // };

//   const viewLeadDetails = (lead) => {
//     setSelectedLead(lead);
//     setIsEditing(false);
//     setShowDetails(true); // Show details panel when a lead is selected
//   };

//   // Add a function to close the details panel
//   const closeDetails = () => {
//     setShowDetails(false);
//     setSelectedLead(null);
//     setIsEditing(false);
//   };

//   // Edit lead
//   const handleEditToggle = () => {
//     setIsEditing(!isEditing);
//   };

//   // Update lead
//   //   const handleUpdateLead = async () => {
//   //     try {
//   //       const token = localStorage.getItem('token');
//   //       await axios.put(`/api/admin/leads/${selectedLead._id}`, selectedLead, {
//   //         headers: { Authorization: `Bearer ${token}` }
//   //       });

//   //       setIsEditing(false);
//   //       setNotification({
//   //         type: 'success',
//   //         message: 'Lead updated successfully'
//   //       });
//   //       fetchLeads(currentPage);
//   //     } catch (error) {
//   //       console.error('Error updating lead:', error);
//   //       setNotification({
//   //         type: 'error',
//   //         message: 'Failed to update lead'
//   //       });
//   //     }
//   //   };
//   // Replace your existing handleUpdateLead function
//   const handleUpdateLead = async () => {
//     try {
//       const token = localStorage.getItem("token");
//       let response;

//       if (selectedLead._id) {
//         // Update existing lead
//         response = await axios.put(
//           `/api/admin/leads/${selectedLead._id}`,
//           selectedLead,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );

//         setNotification({
//           type: "success",
//           message: "Lead updated successfully",
//         });
//       } else {
//         // Create new lead
//         response = await axios.post(`/api/admin/leads`, selectedLead, {
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         setNotification({
//           type: "success",
//           message: "Lead created successfully",
//         });
//       }

//       setIsEditing(false);
//       fetchLeads(currentPage);
//     } catch (error) {
//       console.error("Error saving lead:", error);
//       setNotification({
//         type: "error",
//         message: selectedLead._id
//           ? "Failed to update lead"
//           : "Failed to create lead",
//       });
//     }
//   };

//   // Handle input changes for editing
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setSelectedLead({
//       ...selectedLead,
//       [name]: value,
//     });
//   };

//   const generateMessageTemplates = (lead) => {
//     const name = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
//     const user = JSON.parse(localStorage.getItem("user")) || {};
//     // const adminName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
//     const adminName = `${user.firstName || ""}`.trim();

//     // Email template
//     const emailBody = `
//   Dear ${name},
  
//   Thank you for your interest in ${config.siteName} products/services. I'm reaching out regarding your recent inquiry.
  
//   I'd love to learn more about your needs and how we can assist you. Would you be available for a brief call in the coming days?
  
//   Please let me know what works best for you, or feel free to reach me directly at ${config.contactPhone}.
  
//   Best regards,
//   ${adminName}
//   ${config.siteName}
//     `;

//     // SMS template
//     const smsBody = `Hello ${name}, this is ${adminName} from ${config.siteName}. Thanks for your interest! I'd like to discuss how we can help you. When would be a good time to talk? Reply to this message or call ${config.contactPhone}.`;

//     return { emailBody, smsBody };
//   };

//   // Add this function to handle sending email
//   const handleSendEmail = async () => {
//     try {
//       setSendingMessage(true);
//       const token = localStorage.getItem("token");

//       await axios.post(
//         "/api/admin/leads/send-email",
//         {
//           leadId: selectedLead._id,
//           subject: `Following up on your interest in ${config.siteName} Products`,
//           message: emailTemplate,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       if (selectedLead.status === "new") {
//         // Update lead status to contacted
//         await axios.put(
//           `/api/admin/leads/${selectedLead._id}`,
//           { ...selectedLead, status: "contacted" },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//       }

//       setSendingMessage(false);
//       setMessageSuccess(true);

//       // Close modal and refresh after 2 seconds
//       setTimeout(() => {
//         setShowEmailModal(false);
//         setMessageSuccess(false);
//         fetchLeads(currentPage);
//       }, 2000);
//     } catch (error) {
//       console.error("Error sending email:", error);
//       setSendingMessage(false);
//       setNotification({
//         type: "error",
//         message: "Failed to send email",
//       });
//     }
//   };

//   // Add this function to handle sending SMS
//   const handleSendSms = async () => {
//     try {
//       setSendingMessage(true);
//       const token = localStorage.getItem("token");

//       await axios.post(
//         "/api/admin/leads/send-sms",
//         {
//           leadId: selectedLead._id,
//           message: smsTemplate,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       if (selectedLead.status === "new") {
//         // Update lead status to contacted
//         await axios.put(
//           `/api/admin/leads/${selectedLead._id}`,
//           { ...selectedLead, status: "contacted" },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//       }
//       setSendingMessage(false);
//       setMessageSuccess(true);

//       // Close modal and refresh after 2 seconds
//       setTimeout(() => {
//         setShowSmsModal(false);
//         setMessageSuccess(false);
//         fetchLeads(currentPage);
//       }, 2000);
//     } catch (error) {
//       console.error("Error sending SMS:", error);
//       setSendingMessage(false);
//       setNotification({
//         type: "error",
//         message: "Failed to send SMS",
//       });
//     }
//   };

//   const makeContact = (selectedLead) => {
//     const token = localStorage.getItem("token");

//     if (selectedLead.status === "new") {
//       // Update lead status to contacted
//       axios.put(
//         `/api/admin/leads/${selectedLead._id}`,
//         { ...selectedLead, status: "contacted" },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       // Close modal and refresh after 2 seconds
//     }
//     // setTimeout(() => {
//     //   fetchLeads(currentPage);
//     //   closeDetails();
//     // }, 6000);
//   };

//   const handleOpenAppointment = () => {
//     setAppointmentDate(new Date());
//     setAppointmentTime("10:00");
//     setAppointmentNotes("");
//     setAppointmentStep(1);
//     setShowAppointmentModal(true);
//   };

//   const generateAppointmentTemplates = () => {
//     const name = `${selectedLead.firstName || ""} ${selectedLead.lastName || ""
//       }`.trim();
//     const user = JSON.parse(localStorage.getItem("user")) || {};
//     const adminName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
//     const formattedDate = appointmentDate.toLocaleDateString("en-US", {
//       weekday: "long",
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     });

//     // Email template
//     const emailBody = `
// Dear ${name},

// I'm pleased to confirm your appointment with ${config.siteName
//       } on ${formattedDate} at ${appointmentTime}.

// Meeting details:
// - Date: ${formattedDate}
// - Time: ${appointmentTime}
// ${appointmentNotes ? `- Notes: ${appointmentNotes}` : ""}

// If you need to reschedule or have any questions, please don't hesitate to contact us at ${config.contactPhone
//       }.

// We look forward to speaking with you!

// Best regards,
// ${adminName}
// ${config.siteName}
// ${config.contactAddress}
//   `;

//     // SMS template
//     const smsBody = `Hello ${name}, this is ${adminName} from ${config.siteName}. Your appointment is confirmed for ${formattedDate} at ${appointmentTime}. Please let us know if you need to reschedule. Looking forward to our meeting!`;

//     return { emailBody, smsBody };
//   };

//   const handleStatusChange = async (newStatus, leadObj = null) => {
//     try {
//       const token = localStorage.getItem("token");

//       // Determine which lead to update - either the explicitly passed lead (from list view)
//       // or the currently selectedLead (from detail view)
//       const leadToUpdate = leadObj || selectedLead;

//       if (!leadToUpdate || !leadToUpdate._id) {
//         console.error("No lead ID available for status update");
//         return;
//       }

//       const updatedLead = { ...leadToUpdate, status: newStatus };

//       await axios.put(`/api/admin/leads/${leadToUpdate._id}`, updatedLead, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       // If we're updating the selectedLead, update it in state too
//       if (!leadObj) {
//         setSelectedLead(updatedLead);
//       }

//       setShowStatusDropdown(false);
//       setNotification({
//         type: "success",
//         message: "Lead status updated successfully",
//       });

//       // Refresh leads list to reflect the change
//       fetchLeads(currentPage);
//     } catch (error) {
//       console.error("Error updating lead status:", error);
//       setNotification({
//         type: "error",
//         message: "Failed to update lead status",
//       });
//     }
//   };

//   const handleAppointmentNext = () => {
//     const { emailBody, smsBody } = generateAppointmentTemplates();
//     setAppointmentEmailTemplate(emailBody);
//     setAppointmentSmsTemplate(smsBody);
//     setAppointmentStep(2);
//   };

//   const handleScheduleAppointment = async (sendType) => {
//     try {
//       setSendingMessage(true);
//       const token = localStorage.getItem("token");

//       // First save the appointment data
//       const appointmentData = {
//         leadId: selectedLead._id,
//         date: appointmentDate,
//         time: appointmentTime,
//         notes: appointmentNotes,
//         status: "scheduled",
//       };

//       const response = await axios.post(
//         "/api/admin/leads/appointments",
//         appointmentData,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       const appointmentId = response.data._id;

//       // Then send notification
//       if (sendType === "email" && selectedLead.email) {
//         await axios.post(
//           "/api/admin/leads/send-email",
//           {
//             leadId: selectedLead._id,
//             appointmentId,
//             subject: `Your Appointment Confirmation - ${config.siteName}`,
//             message: appointmentEmailTemplate,
//           },
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );
//       } else if (sendType === "sms" && selectedLead.phone) {
//         await axios.post(
//           "/api/admin/leads/send-sms",
//           {
//             leadId: selectedLead._id,
//             appointmentId,
//             message: appointmentSmsTemplate,
//           },
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );
//       }

//       // Update lead status to qualified
//       await axios.put(
//         `/api/admin/leads/${selectedLead._id}`,
//         { ...selectedLead, status: "qualified" },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       setSendingMessage(false);
//       setMessageSuccess(true);

//       // Close modal and refresh after 2 seconds
//       setTimeout(() => {
//         setShowAppointmentModal(false);
//         setMessageSuccess(false);
//         fetchLeads(currentPage);
//       }, 2000);
//     } catch (error) {
//       console.error("Error scheduling appointment:", error);
//       setSendingMessage(false);
//       setNotification({
//         type: "error",
//         message: "Failed to schedule appointment",
//       });
//     }
//   };

//   const handleSetReminders = async () => {
//     try {
//       const token = localStorage.getItem("token");

//       await axios.post(
//         "/api/admin/leads/appointment-reminders",
//         {
//           leadId: selectedLead._id,
//           appointmentId: selectedLead.appointment._id,
//           reminderType,
//           reminderTimes:
//             reminderType === "schedule"
//               ? [
//                 { hours: 72 }, // 3 days before
//                 { hours: 24 }, // 1 day before
//                 { hours: 1 }, // 1 hour before
//               ]
//               : [{ hours: 0 }], // Immediate
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       setNotification({
//         type: "success",
//         message:
//           reminderType === "schedule"
//             ? "Reminders scheduled successfully"
//             : "Reminder sent successfully",
//       });

//       setAppointment(true);
//       setShowReminderModal(false);
//     } catch (error) {
//       console.error("Error setting reminders:", error);
//       setNotification({
//         type: "error",
//         message: "Failed to set reminders",
//       });
//     }
//   };

//   const handleAddQuestion = () => {
//     if (!newQuestion.trim()) return;

//     const newId = Math.max(0, ...questions.map((q) => q.id)) + 1;
//     setQuestions([...questions, { id: newId, text: newQuestion, answer: "" }]);
//     setNewQuestion("");
//   };

//   const handleEditQuestion = (id, newText) => {
//     setQuestions(
//       questions.map((q) => (q.id === id ? { ...q, text: newText } : q))
//     );
//   };

//   const handleDeleteQuestion = (id) => {
//     setQuestions(questions.filter((q) => q.id !== id));
//   };

//   const handleAnswerChange = (id, answer) => {
//     setQuestions(questions.map((q) => (q.id === id ? { ...q, answer } : q)));
//   };

//   const handleSaveSurvey = async () => {
//     try {
//       const token = localStorage.getItem("token");

//       // Filter out unanswered questions
//       const answeredQuestions = questions.filter((q) => q.answer.trim());

//       if (answeredQuestions.length === 0) {
//         setNotification({
//           type: "error",
//           message: "Please answer at least one question",
//         });
//         return;
//       }

//       await axios.post(
//         "/api/admin/leads/survey",
//         {
//           leadId: selectedLead._id,
//           questions: answeredQuestions,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       setNotification({
//         type: "success",
//         message: "Survey saved successfully",
//       });

//       setShowSurveyModal(false);
//       fetchLeads(currentPage);
//     } catch (error) {
//       console.error("Error saving survey:", error);
//       setNotification({
//         type: "error",
//         message: "Failed to save survey",
//       });
//     }
//   };

//   // Export leads as CSV
//   const exportLeads = () => {
//     // Convert leads to CSV format
//     const headers = [
//       "First Name",
//       "Last Name",
//       "Email",
//       "Phone",
//       "Company",
//       "Source",
//       "Status",
//       "Created Date",
//     ];
//     const csvRows = [];

//     // Add headers
//     csvRows.push(headers.join(","));

//     // Add data rows
//     leads.forEach((lead) => {
//       const row = [
//         lead.firstName || "",
//         lead.lastName || "",
//         lead.email || "",
//         lead.phone || "",
//         lead.company || "",
//         lead.source || "",
//         lead.status || "",
//         new Date(lead.createdAt).toLocaleDateString(),
//       ];

//       // Escape commas in fields
//       const csvRow = row
//         .map((field) => {
//           // If field contains comma, wrap in quotes
//           return typeof field === "string" && field.includes(",")
//             ? `"${field}"`
//             : field;
//         })
//         .join(",");

//       csvRows.push(csvRow);
//     });

//     // Create and download CSV file
//     const csvContent = csvRows.join("\n");
//     const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
//     const url = URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.setAttribute("href", url);
//     link.setAttribute(
//       "download",
//       `leads-export-${new Date().toISOString().slice(0, 10)}.csv`
//     );
//     link.style.visibility = "hidden";
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   return (
//     <div className="space-y-6">
//       {/* Notifications */}
//       {notification && (
//         <div
//           className={`p-4 rounded-md ${notification.type === "success"
//             ? "bg-green-50 text-green-700"
//             : "bg-red-50 text-red-700"
//             }`}
//         >
//           <div className="flex items-center">
//             {notification.type === "success" ? (
//               <CheckCircle className="w-5 h-5 mr-2" />
//             ) : (
//               <AlertCircle className="w-5 h-5 mr-2" />
//             )}
//             <span>{notification.message}</span>
//             <button
//               className="ml-auto text-gray-500 hover:text-gray-700"
//               onClick={() => setNotification(null)}
//             >
//               ×
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Header and filters */}
//       <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
//         <h2 className="text-2xl font-semibold mb-4 md:mb-0">Lead Management</h2>
//         <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
//           <button
//             onClick={() => {
//               setSelectedLead({
//                 firstName: "",
//                 lastName: "",
//                 email: "",
//                 phone: "",
//                 company: "",
//                 title: "",
//                 status: "new",
//                 source: "manual",
//                 notes: "",
//               });
//               setIsEditing(true);
//               setShowDetails(true);
//             }}
//             className="flex items-center px-3 py-2 bg-blue-600 text-white border border-blue-700 rounded-md hover:bg-blue-700"
//           >
//             <UserPlus className="w-4 h-4 mr-1" />
//             Add Lead
//           </button>
//         </div>
//       </div>

//       {/* Filters section */}
//       {!showDetails && (
//         <div className="flex flex-col lg:flex-row gap-2 w-full lg:w-auto">
//           <div className="relative">
//             <input
//               type="text"
//               placeholder="Search leads..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full"
//             />
//             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//               <svg
//                 className="h-5 w-5 text-gray-400"
//                 fill="none"
//                 viewBox="0 0 24 24"
//                 stroke="currentColor"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth="2"
//                   d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
//                 />
//               </svg>
//             </div>
//           </div>

//           <div className="flex flex-wrap gap-2">
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="pr-7 pl-2 py-2 border border-gray-300 rounded-md"
//             >
//               <option value="">All Statuses</option>
//               <option value="new">New</option>
//               <option value="contacted">Contacted</option>
//               <option value="qualified">Qualified</option>
//               <option value="converted">Converted</option>
//               <option value="disqualified">Disqualified</option>
//             </select>

//             <select
//               value={sourceFilter}
//               onChange={(e) => setSourceFilter(e.target.value)}
//               className="pr-7 pl-2 py-2 border border-gray-300 rounded-md"
//             >
//               <option value="">All Sources</option>
//               <option value="meta">Meta</option>
//               <option value="instagram">Instagram</option>
//               <option value="facebook">Facebook</option>
//               <option value="website">Website</option>
//               <option value="manual">Manual</option>
//               <option value="other">Other</option>
//             </select>

//             <button
//               onClick={exportLeads}
//               className="flex font-normal items-center px-2 pr-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
//             >
//               <Download className="w-4 h-4 mr-1" />
//               Export
//             </button>

//             <button
//               onClick={() => fetchLeads(currentPage)}
//               className="flex font-normal items-center px-2 pr-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
//             >
//               <RefreshCw className="w-4 h-4 mr-1" />
//               Refresh
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Main content */}
//       <div className="bg-white rounded-lg shadow-md">
//         {/* Conditionally render either leads list or lead details based on state */}
//         {!showDetails ? (
//           <>
//             {/* Leads list panel */}
//             <div className="p-4 border-b border-gray-200">
//               <h3 className="text-lg font-medium">Leads</h3>
//             </div>

//             {loading ? (
//               <div className="p-8 flex justify-center">
//                 <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
//               </div>
//             ) : leads.length === 0 ? (
//               <div className="p-8 text-center text-gray-500">
//                 <p>No leads found. Try adjusting your filters.</p>
//               </div>
//             ) : (
//               <div>
//                 {/* Header row - desktop only */}
//                 <div className="hidden lg:grid lg:grid-cols-12 bg-gray-50 py-3 px-4 border-b border-gray-200">
//                   <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Name
//                   </div>
//                   <div className="col-span-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Contact
//                   </div>
//                   <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Source
//                   </div>
//                   <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Status
//                   </div>
//                   <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Created
//                   </div>
//                   <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Actions
//                   </div>
//                 </div>

//                 {/* Lead rows */}
//                 <div className="divide-y divide-gray-200">
//                   {leads.map((lead) => (
//                     <div
//                       key={lead._id}
//                       className="py-4 px-4 hover:bg-gray-50 cursor-pointer"
//                       onClick={() => viewLeadDetails(lead)}
//                     >
//                       <div className="lg:grid lg:grid-cols-12 lg:gap-4 lg:items-center">
//                         {/* Name & Company */}
//                         <div className="flex items-center mb-3 lg:mb-0 lg:col-span-2">
//                           <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
//                             <User className="h-5 w-5 text-gray-500" />
//                           </div>
//                           <div className="ml-3">
//                             <div className="text-sm font-medium text-gray-900">
//                               {lead.firstName} {lead.lastName}
//                             </div>
//                             <div className="text-sm text-gray-500">
//                               {lead.company || "No company"}
//                             </div>
//                           </div>
//                         </div>

//                         {/* Contact Info */}
//                         <div className="mb-3 lg:mb-0 lg:col-span-3">
//                           <div className="text-sm text-gray-900 lg:mb-1">
//                             <span className="lg:hidden text-xs text-gray-500 mr-1">
//                               Email:
//                             </span>
//                             {lead.email ? (
//                               <span className="truncate block">
//                                 {lead.email}
//                               </span>
//                             ) : (
//                               "No email"
//                             )}
//                           </div>
//                           <div className="text-sm text-gray-500">
//                             <span className="lg:hidden text-xs text-gray-500 mr-1">
//                               Phone:
//                             </span>
//                             {lead.phone || "No phone"}
//                           </div>
//                         </div>

//                         {/* Source */}
//                         <div className="mb-3 lg:mb-0 lg:col-span-2">
//                           <span className="lg:hidden text-xs text-gray-500 mr-1">
//                             Source:
//                           </span>
//                           <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
//                             {lead.source}
//                           </span>
//                         </div>

//                         {/* Status */}
//                         {/* <div className="mb-3 lg:mb-0 lg:col-span-2">
//                       <span className="lg:hidden text-xs text-gray-500 mr-1">Status:</span>
//                       <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
//                         lead.status === 'new' ? 'bg-green-100 text-green-800' :
//                         lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
//                         lead.status === 'qualified' ? 'bg-blue-100 text-blue-800' :
//                         lead.status === 'converted' ? 'bg-purple-100 text-purple-800' :
//                         'bg-gray-100 text-gray-800'
//                       }`}>
//                         {lead.status || 'New'}
//                       </span>
//                     </div> */}
//                         <div className="mb-3 lg:mb-0 lg:col-span-2">
//                           <span className="lg:hidden text-xs text-gray-500 mr-1">
//                             Status:
//                           </span>
//                           <div
//                             className="relative inline-block"
//                             id={`status-dropdown-${lead._id}`}
//                           >
//                             <div
//                               onClick={(e) => {
//                                 e.stopPropagation(); // Prevent row click event
//                                 setSelectedLead(lead);
//                                 setShowStatusDropdown(lead._id);
//                               }}
//                               className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded border border-gray-200 inline-flex items-center"
//                             >
//                               <span
//                                 className={`w-2 h-2 rounded-full mr-2 ${lead.status === "new"
//                                   ? "bg-green-500"
//                                   : lead.status === "contacted"
//                                     ? "bg-yellow-500"
//                                     : lead.status === "qualified"
//                                       ? "bg-blue-500"
//                                       : lead.status === "converted"
//                                         ? "bg-purple-500"
//                                         : "bg-gray-500"
//                                   }`}
//                               ></span>
//                               <span className="text-xs font-medium capitalize">
//                                 {lead.status || "New"}
//                               </span>
//                               <svg
//                                 className="h-3 w-3 ml-1 text-gray-500"
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 viewBox="0 0 20 20"
//                                 fill="currentColor"
//                               >
//                                 <path
//                                   fillRule="evenodd"
//                                   d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
//                                   clipRule="evenodd"
//                                 />
//                               </svg>
//                             </div>

//                             {/* Status dropdown for this specific lead */}
//                             {showStatusDropdown === lead._id && (
//                               <div
//                                 className="absolute z-50 w-36 bg-white rounded-md shadow-lg py-1 mt-1 border border-gray-200 right-0"
//                                 onClick={(e) => e.stopPropagation()}
//                               >
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleStatusChange("new", lead);
//                                   }}
//                                   style={{ boxShadow: "none" }}
//                                   className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "new" ? "bg-gray-50" : ""
//                                     }`}
//                                 >
//                                   <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
//                                   New
//                                 </button>
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleStatusChange("contacted", lead);
//                                   }}
//                                   style={{ boxShadow: "none" }}
//                                   className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "contacted"
//                                     ? "bg-gray-50"
//                                     : ""
//                                     }`}
//                                 >
//                                   <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-2"></span>
//                                   Contacted
//                                 </button>
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleStatusChange("qualified", lead);
//                                   }}
//                                   style={{ boxShadow: "none" }}
//                                   className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "qualified"
//                                     ? "bg-gray-50"
//                                     : ""
//                                     }`}
//                                 >
//                                   <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
//                                   Qualified
//                                 </button>
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleStatusChange("converted", lead);
//                                   }}
//                                   style={{ boxShadow: "none" }}
//                                   className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "converted"
//                                     ? "bg-gray-50"
//                                     : ""
//                                     }`}
//                                 >
//                                   <span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
//                                   Converted
//                                 </button>
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleStatusChange("disqualified", lead);
//                                   }}
//                                   style={{ boxShadow: "none" }}
//                                   className={`w-full font-normal text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${lead.status === "disqualified"
//                                     ? "bg-gray-50"
//                                     : ""
//                                     }`}
//                                 >
//                                   <span className="inline-block w-2 h-2 rounded-full bg-gray-500 mr-2"></span>
//                                   Disqualified
//                                 </button>
//                               </div>
//                             )}
//                           </div>
//                         </div>

//                         {/* Created Date */}
//                         <div className="mb-3 lg:mb-0 lg:col-span-1">
//                           <span className="lg:hidden text-xs text-gray-500 mr-1">
//                             Created:
//                           </span>
//                           <span className="text-sm text-gray-500">
//                             {new Date(lead.createdAt).toLocaleDateString()}
//                           </span>
//                         </div>

//                         {/* Actions */}
//                         <div className="flex justify-end sm:justify-end md:justify-end lg:justify-center lg:ml-8 xl:ml-14 lg:col-span-2 mr-6">
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               viewLeadDetails(lead);
//                             }}
//                             className="text-blue-600 hover:text-blue-900 p-1 "
//                             style={{
//                               boxShadow: "none",
//                               border: "none",
//                               transform: "none",
//                             }}
//                           >
//                             <Eye className="w-5 h-5" />
//                           </button>
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setSelectedLead(lead);
//                               setIsEditing(true);
//                               setShowDetails(true);
//                             }}
//                             className="text-green-600 hover:text-green-900 p-1 "
//                             style={{
//                               boxShadow: "none",
//                               border: "none",
//                               transform: "none",
//                             }}
//                           >
//                             <Edit className="w-5 h-5" />
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
//                 <div className="flex-1 flex justify-between sm:hidden">
//                   <button
//                     onClick={() => fetchLeads(currentPage - 1)}
//                     disabled={currentPage === 1}
//                     className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1
//                       ? "bg-gray-100 text-gray-400"
//                       : "bg-white text-gray-700 hover:bg-gray-50"
//                       }`}
//                   >
//                     Previous
//                   </button>
//                   <button
//                     onClick={() => fetchLeads(currentPage + 1)}
//                     disabled={currentPage === totalPages}
//                     className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages
//                       ? "bg-gray-100 text-gray-400"
//                       : "bg-white text-gray-700 hover:bg-gray-50"
//                       }`}
//                   >
//                     Next
//                   </button>
//                 </div>
//                 <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
//                   <div>
//                     <p className="text-sm text-gray-700">
//                       Showing{" "}
//                       <span className="font-medium">
//                         {(currentPage - 1) * leads.length + 1}
//                       </span>{" "}
//                       to{" "}
//                       <span className="font-medium">
//                         {Math.min(
//                           currentPage * leads.length,
//                           totalPages * leads.length
//                         )}
//                       </span>{" "}
//                       of{" "}
//                       <span className="font-medium">
//                         {totalPages * leads.length}
//                       </span>{" "}
//                       results
//                     </p>
//                   </div>
//                   <div>
//                     <nav
//                       className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
//                       aria-label="Pagination"
//                     >
//                       <button
//                         onClick={() => fetchLeads(currentPage - 1)}
//                         disabled={currentPage === 1}
//                         className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${currentPage === 1
//                           ? "bg-gray-100 text-gray-400"
//                           : "bg-white text-gray-700 hover:bg-gray-50"
//                           }`}
//                       >
//                         <span className="sr-only">Previous</span>
//                         <svg
//                           className="h-5 w-5"
//                           xmlns="http://www.w3.org/2000/svg"
//                           viewBox="0 0 20 20"
//                           fill="currentColor"
//                           aria-hidden="true"
//                         >
//                           <path
//                             fillRule="evenodd"
//                             d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
//                             clipRule="evenodd"
//                           />
//                         </svg>
//                       </button>

//                       {/* Page numbers */}
//                       {Array.from({ length: totalPages }, (_, i) => i + 1).map(
//                         (page) => (
//                           <button
//                             key={page}
//                             onClick={() => fetchLeads(page)}
//                             className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${page === currentPage
//                               ? "bg-blue-50 text-blue-600 border-blue-300 z-10"
//                               : "bg-white text-gray-700 hover:bg-gray-50"
//                               }`}
//                           >
//                             {page}
//                           </button>
//                         )
//                       )}

//                       <button
//                         onClick={() => fetchLeads(currentPage + 1)}
//                         disabled={currentPage === totalPages}
//                         className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${currentPage === totalPages
//                           ? "bg-gray-100 text-gray-400"
//                           : "bg-white text-gray-700 hover:bg-gray-50"
//                           }`}
//                       >
//                         <span className="sr-only">Next</span>
//                         <svg
//                           className="h-5 w-5"
//                           xmlns="http://www.w3.org/2000/svg"
//                           viewBox="0 0 20 20"
//                           fill="currentColor"
//                           aria-hidden="true"
//                         >
//                           <path
//                             fillRule="evenodd"
//                             d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
//                             clipRule="evenodd"
//                           />
//                         </svg>
//                       </button>
//                     </nav>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </>
//         ) : (
//           /* Lead details panel */
//           <div>
//             <div className="p-4 border-b border-gray-200 flex justify-between items-center">
//               <h3 className="text-lg font-medium">
//                 {!selectedLead
//                   ? "Select a Lead"
//                   : selectedLead._id
//                     ? isEditing
//                       ? "Edit Lead"
//                       : "Lead Details"
//                     : "Add New Lead"}
//               </h3>
//               <div className="flex space-x-2">
//                 {selectedLead && !isEditing && (
//                   <button
//                     onClick={handleEditToggle}
//                     className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100"
//                   >
//                     <Edit className="w-4 h-4 mr-1" />
//                     Edit
//                   </button>
//                 )}
//                 <button
//                   onClick={closeDetails}
//                   className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100"
//                 >
//                   <XIcon className="w-4 h-4 mr-1" />
//                   Close
//                 </button>
//               </div>
//             </div>

//             <div className="p-4">
//               {isEditing ? (
//                 <div className="space-y-4">
//                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         First Name
//                       </label>
//                       <input
//                         type="text"
//                         name="firstName"
//                         value={selectedLead.firstName || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       />
//                     </div>
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Last Name
//                       </label>
//                       <input
//                         type="text"
//                         name="lastName"
//                         value={selectedLead.lastName || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Email
//                       </label>
//                       <input
//                         type="email"
//                         name="email"
//                         value={selectedLead.email || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Phone
//                       </label>
//                       <input
//                         type="text"
//                         name="phone"
//                         value={selectedLead.phone || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Company
//                       </label>
//                       <input
//                         type="text"
//                         name="company"
//                         value={selectedLead.company || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Title
//                       </label>
//                       <input
//                         type="text"
//                         name="title"
//                         value={selectedLead.title || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Status
//                       </label>
//                       <select
//                         name="status"
//                         value={selectedLead.status || "new"}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       >
//                         <option value="new">New</option>
//                         <option value="contacted">Contacted</option>
//                         <option value="qualified">Qualified</option>
//                         <option value="converted">Converted</option>
//                         <option value="disqualified">Disqualified</option>
//                       </select>
//                     </div>

//                     {/* new field */}
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Source
//                       </label>
//                       <select
//                         name="source"
//                         value={selectedLead.source || "manual"}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       >
//                         <option value="manual">Manual Entry</option>
//                         <option value="meta">Meta</option>
//                         <option value="instagram">Instagram</option>
//                         <option value="facebook">Facebook</option>
//                         <option value="website">Website</option>
//                         <option value="other">Other</option>
//                       </select>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">
//                         Notes
//                       </label>
//                       <textarea
//                         name="notes"
//                         rows="4"
//                         value={selectedLead.notes || ""}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
//                       ></textarea>
//                     </div>
//                   </div>

//                   <div className="flex justify-end space-x-3">
//                     <button
//                       onClick={() => {
//                         setIsEditing(false);
//                         if (!selectedLead._id) {
//                           // Clear form if this was a new lead
//                           setSelectedLead(null);
//                           setShowDetails(false);
//                         }
//                       }}
//                       className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       onClick={() => {
//                         handleUpdateLead();
//                         // After saving, you might want to show the updated lead details
//                         setTimeout(() => {
//                           setIsEditing(false);
//                           fetchLeads(currentPage);
//                         }, 300);
//                       }}
//                       className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
//                     >
//                       {selectedLead._id ? "Save Changes" : "Create Lead"}
//                     </button>
//                   </div>
//                 </div>
//               ) : (
//                 <div className="space-y-4">
//                   <div className="block lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:gap-6">
//                     {/* Name field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Name
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         {selectedLead.firstName} {selectedLead.lastName}
//                       </div>
//                     </div>

//                     {/* Email field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Email
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         {selectedLead.email ? (
//                           <a
//                             href={`mailto:${selectedLead.email}`}
//                             className="flex items-center text-blue-600 hover:underline"
//                           >
//                             <Mail className="w-4 h-4 mr-1" />
//                             {selectedLead.email}
//                           </a>
//                         ) : (
//                           "No email provided"
//                         )}
//                       </div>
//                     </div>

//                     {/* Phone field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Phone
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         {selectedLead.phone ? (
//                           <a
//                             href={`tel:${selectedLead.phone}`}
//                             className="flex items-center text-blue-600 hover:underline"
//                           >
//                             <Phone className="w-4 h-4 mr-1" />
//                             {selectedLead.phone}
//                           </a>
//                         ) : (
//                           "No phone provided"
//                         )}
//                       </div>
//                     </div>

//                     {/* Company field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Company
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         {selectedLead.company ? (
//                           <div className="flex items-center">
//                             <Building className="w-4 h-4 mr-1" />
//                             {selectedLead.company}
//                           </div>
//                         ) : (
//                           "No company provided"
//                         )}
//                       </div>
//                     </div>

//                     {/* Source field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Source
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
//                           {selectedLead.source}
//                         </span>
//                       </div>
//                     </div>

//                     {/* Status field */}
//                     {/* <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                     <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">Status</div>
//                     <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                       <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
//                         selectedLead.status === 'new' ? 'bg-green-100 text-green-800' :
//                         selectedLead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
//                         selectedLead.status === 'qualified' ? 'bg-blue-100 text-blue-800' :
//                         selectedLead.status === 'converted' ? 'bg-purple-100 text-purple-800' :
//                         'bg-gray-100 text-gray-800'
//                       }`}>
//                         {selectedLead.status || 'New'}
//                       </span>
//                     </div>
//                   </div> */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-visible">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Status
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         <div
//                           className="relative inline-block w-full"
//                           id="status-dropdown-container"
//                         >
//                           <div
//                             onClick={() =>
//                               !isEditing &&
//                               setShowStatusDropdown(!showStatusDropdown)
//                             }
//                             className={`inline-flex items-center justify-between ${isEditing
//                               ? "cursor-not-allowed opacity-70"
//                               : "cursor-pointer hover:bg-gray-50"
//                               } rounded px-2 py-1 w-40 border border-gray-200`}
//                           >
//                             <div className="flex items-center">
//                               <span
//                                 className={`w-3 h-3 rounded-full mr-2 ${selectedLead.status === "new"
//                                   ? "bg-green-500"
//                                   : selectedLead.status === "contacted"
//                                     ? "bg-yellow-500"
//                                     : selectedLead.status === "qualified"
//                                       ? "bg-blue-500"
//                                       : selectedLead.status === "converted"
//                                         ? "bg-purple-500"
//                                         : "bg-gray-500"
//                                   }`}
//                               ></span>
//                               <span className="text-sm font-medium capitalize">
//                                 {selectedLead.status || "New"}
//                               </span>
//                             </div>
//                             {!isEditing && (
//                               <svg
//                                 className="h-4 w-4 text-gray-500"
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 viewBox="0 0 20 20"
//                                 fill="currentColor"
//                               >
//                                 <path
//                                   fillRule="evenodd"
//                                   d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
//                                   clipRule="evenodd"
//                                 />
//                               </svg>
//                             )}
//                           </div>

//                           {/* Status dropdown - improved version */}
//                           {showStatusDropdown && !isEditing && (
//                             <div
//                               className="absolute z-50 w-40 bg-white rounded-md shadow-lg py-1 mt-1 border border-gray-200 left-0"
//                               style={{ position: "absolute", top: "100%" }}
//                               onClick={(e) => e.stopPropagation()}
//                             >
//                               <button
//                                 onClick={() => handleStatusChange("new")}
//                                 style={{ boxShadow: "none" }}
//                                 className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "new"
//                                   ? "bg-gray-50"
//                                   : ""
//                                   }`}
//                               >
//                                 <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
//                                 New
//                               </button>
//                               <button
//                                 onClick={() => handleStatusChange("contacted")}
//                                 style={{ boxShadow: "none" }}
//                                 className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "contacted"
//                                   ? "bg-gray-50"
//                                   : ""
//                                   }`}
//                               >
//                                 <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>
//                                 Contacted
//                               </button>
//                               <button
//                                 onClick={() => handleStatusChange("qualified")}
//                                 style={{ boxShadow: "none" }}
//                                 className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "qualified"
//                                   ? "bg-gray-50"
//                                   : ""
//                                   }`}
//                               >
//                                 <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
//                                 Qualified
//                               </button>
//                               <button
//                                 onClick={() => handleStatusChange("converted")}
//                                 style={{ boxShadow: "none" }}
//                                 className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "converted"
//                                   ? "bg-gray-50"
//                                   : ""
//                                   }`}
//                               >
//                                 <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-2"></span>
//                                 Converted
//                               </button>
//                               <button
//                                 onClick={() =>
//                                   handleStatusChange("disqualified")
//                                 }
//                                 style={{ boxShadow: "none" }}
//                                 className={`w-full font-normal text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${selectedLead.status === "disqualified"
//                                   ? "bg-gray-50"
//                                   : ""
//                                   }`}
//                               >
//                                 <span className="inline-block w-3 h-3 rounded-full bg-gray-500 mr-2"></span>
//                                 Disqualified
//                               </button>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>

//                     {/* Title field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Title
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3">
//                         {selectedLead.title || "N/A"}
//                       </div>
//                     </div>

//                     {/* Created field */}
//                     <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Created
//                       </div>
//                       <div className="text-sm text-gray-900 lg:px-4 lg:py-3 flex items-center">
//                         <Calendar className="w-4 h-4 mr-1" />
//                         {new Date(selectedLead.createdAt).toLocaleDateString()}
//                         <Clock className="w-4 h-4 ml-2 mr-1" />
//                         {new Date(selectedLead.createdAt).toLocaleTimeString()}
//                       </div>
//                     </div>

//                     {/* Source ID field (conditional) */}
//                     {selectedLead.sourceId && (
//                       <div className="pt-3 md:pt-3 lg:pt-0 flex justify-between border-b pb-3 lg:flex-col lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:pb-0 lg:overflow-hidden">
//                         <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                           Source ID
//                         </div>
//                         <div className="text-sm text-gray-900 lg:px-4 lg:py-3 flex items-center">
//                           <ExternalLink className="w-4 h-4 mr-1" />
//                           {selectedLead.sourceId}
//                         </div>
//                       </div>
//                     )}
//                   </div>

//                   {/* Notes section (kept separate since it's longer content) */}
//                   {selectedLead.notes && (
//                     <div className="pt-3 md:pt-3 lg:pt-0 pt-2 mt-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow-sm lg:overflow-hidden">
//                       <div className="text-sm font-medium text-gray-500 lg:bg-gray-50 lg:px-4 lg:py-2 lg:border-b">
//                         Notes
//                       </div>
//                       <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm text-gray-700 lg:bg-white lg:mt-0">
//                         {selectedLead.notes}
//                       </div>
//                     </div>
//                   )}

//                   <div className="flex justify-between pt-4">
//                     {/* for new leads */}
//                     {!isEditing && selectedLead && (
//                       <div className="space-x-2">
//                         {selectedLead.email && (
//                           <button
//                             onClick={() => {
//                               const { emailBody } =
//                                 generateMessageTemplates(selectedLead);
//                               setEmailTemplate(emailBody);
//                               setShowEmailModal(true);
//                             }}
//                             className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium border border-blue-300 rounded-md hover:bg-blue-200"
//                           >
//                             <Mail className="w-4 h-4 mr-1" />
//                             Send Email
//                           </button>
//                         )}
//                         {selectedLead.phone && (
//                           <button
//                             onClick={() => {
//                               const { smsBody } =
//                                 generateMessageTemplates(selectedLead);
//                               setSmsTemplate(smsBody);
//                               setShowSmsModal(true);
//                             }}
//                             className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 text-sm font-medium rounded-md"
//                           >
//                             <MessageSquare className="w-4 h-4 mr-1" />
//                             Send SMS
//                           </button>
//                         )}
//                         {selectedLead.phone && (
//                           <button
//                             className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
//                             onClick={() => {
//                               makeContact(selectedLead);
//                             }}
//                           >
//                             <a
//                               className="inline-flex items-center"
//                               href={`tel:${selectedLead.phone}`}
//                             >
//                               <Phone className="w-4 h-4 mr-1" />
//                               Call
//                             </a>
//                           </button>
//                         )}
//                       </div>
//                     )}
//                   </div>

//                   <div className="flex justify-between pt-4">
//                     <div className="space-x-2">
//                       {/*contacted leads */}
//                       {!isEditing &&
//                         selectedLead &&
//                         selectedLead.status !== "new" && (
//                           <button
//                             onClick={handleOpenAppointment}
//                             className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 border border-purple-300 rounded-md hover:bg-purple-200 text-sm font-medium"
//                           >
//                             <Calendar className="w-4 h-4 mr-1" />
//                             <span className="hidden md:inline">Send&nbsp;</span>
//                             Appointment
//                           </button>
//                         )}

//                       {/* leads with appointments */}
//                       {!isEditing &&
//                         selectedLead &&
//                         selectedLead.status !== "new" && (
//                           <button
//                             onClick={() => setShowReminderModal(true)}
//                             className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 border border-yellow-300 rounded-md hover:bg-yellow-200 text-sm font-medium"
//                           >
//                             <AlarmClock className="w-4 h-4 mr-1" />
//                             <span className="hidden md:inline">
//                               Send&nbsp;
//                             </span>{" "}
//                             Reminder
//                           </button>
//                         )}

//                       {/* contacted leads survey /q*/}
//                       {!isEditing &&
//                         selectedLead &&
//                         selectedLead.status !== "new" && (
//                           <button
//                             onClick={() => setShowSurveyModal(true)}
//                             className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-teal-100 text-teal-700 border border-teal-300 hover:bg-teal-200"
//                           >
//                             <Clipboard className="w-4 h-4 mr-1" />
//                             Survey
//                             <span className="hidden lg:inline">
//                               &nbsp;Questions
//                             </span>
//                           </button>
//                         )}
//                     </div>
//                   </div>

//                   {/* display appointment details */}
//                   {!isEditing &&
//                     selectedLead &&
//                     selectedLead.appointment &&
//                     selectedLead.status !== "new" && (
//                       <div className="space-x-2">
//                         <h4 className="font-medium text-gray-900 mb-2 flex items-center">
//                           <CalendarIcon className="w-4 h-4 mr-1 text-purple-600" />
//                           Scheduled Appointment
//                         </h4>
//                         <div className="bg-purple-50 rounded-md p-3 text-sm">
//                           <div className="flex items-center mb-1">
//                             <Calendar className="w-4 h-4 mr-1 text-purple-600" />
//                             <span>
//                               {new Date(
//                                 selectedLead.appointment.date
//                               ).toLocaleDateString("en-US", {
//                                 weekday: "long",
//                                 month: "long",
//                                 day: "numeric",
//                                 year: "numeric",
//                               })}
//                             </span>
//                           </div>
//                           <div className="flex items-center mb-1">
//                             <Clock className="w-4 h-4 mr-1 text-purple-600" />
//                             <span>{selectedLead.appointment.time}</span>
//                           </div>
//                           {selectedLead.appointment.notes && (
//                             <div className="mt-2 text-gray-700">
//                               <p className="italic">
//                                 {selectedLead.appointment.notes}
//                               </p>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}

//                   {/* Add this to display survey data */}
//                   {!isEditing &&
//                     selectedLead &&
//                     selectedLead.survey &&
//                     selectedLead.survey.length > 0 && (
//                       <div className="mt-4 border-t pt-4">
//                         <h4 className="font-medium text-gray-900 mb-2">
//                           Survey Responses
//                         </h4>
//                         <div className="space-y-2">
//                           {selectedLead.survey.map((item, index) => (
//                             <div
//                               key={index}
//                               className="bg-teal-50 rounded-md p-3"
//                             >
//                               <p className="font-medium text-teal-800">
//                                 {item.text}
//                               </p>
//                               <p className="text-gray-700 mt-1">
//                                 {item.answer}
//                               </p>
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     )}
//                 </div>
//               )}
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Email Modal */}
//       {showEmailModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
//             <div className="flex justify-between items-center p-6 border-b">
//               <h3 className="text-lg font-medium">
//                 Send Email to {selectedLead.firstName} {selectedLead.lastName}
//               </h3>
//               <button
//                 onClick={() => setShowEmailModal(false)}
//                 className="text-gray-400 hover:text-gray-500"
//               >
//                 <XIcon className="h-5 w-5" />
//               </button>
//             </div>

//             {messageSuccess ? (
//               <div className="flex-1 flex flex-col items-center justify-center p-8">
//                 <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
//                   <CheckCircle className="h-10 w-10 text-green-500" />
//                 </div>
//                 <p className="text-lg font-medium">Email sent successfully!</p>
//                 <p className="text-gray-500 mt-1">
//                   Lead status updated to "Contacted"
//                 </p>
//               </div>
//             ) : (
//               <div className="flex-1 overflow-auto p-6">
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     To:
//                   </label>
//                   <div className="p-2 bg-gray-100 rounded text-sm">
//                     {selectedLead.email}
//                   </div>
//                 </div>
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Subject:
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full p-2 border rounded"
//                     defaultValue={`Following up on your interest in ${config.siteName} products`}
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Message:
//                   </label>
//                   <textarea
//                     rows={10}
//                     className="w-full p-2 border rounded"
//                     value={emailTemplate}
//                     onChange={(e) => setEmailTemplate(e.target.value)}
//                   ></textarea>
//                 </div>
//               </div>
//             )}

//             <div className="p-6 border-t flex justify-end space-x-3">
//               {!messageSuccess && (
//                 <>
//                   <button
//                     onClick={() => setShowEmailModal(false)}
//                     className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={handleSendEmail}
//                     disabled={sendingMessage}
//                     className={`px-4 py-2 rounded-md text-white ${sendingMessage
//                       ? "bg-blue-400"
//                       : "bg-blue-600 hover:bg-blue-700"
//                       }`}
//                   >
//                     {sendingMessage ? (
//                       <span className="flex items-center">
//                         <svg
//                           className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                           xmlns="http://www.w3.org/2000/svg"
//                           fill="none"
//                           viewBox="0 0 24 24"
//                         >
//                           <circle
//                             className="opacity-25"
//                             cx="12"
//                             cy="12"
//                             r="10"
//                             stroke="currentColor"
//                             strokeWidth="4"
//                           ></circle>
//                           <path
//                             className="opacity-75"
//                             fill="currentColor"
//                             d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                           ></path>
//                         </svg>
//                         Sending...
//                       </span>
//                     ) : (
//                       "Send Email"
//                     )}
//                   </button>
//                 </>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Appointment Modal */}
//       {showAppointmentModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
//             <div className="flex justify-between items-center p-6 border-b">
//               <h3 className="text-lg font-medium">
//                 {appointmentStep === 1
//                   ? "Schedule Appointment"
//                   : "Confirm Appointment"}
//               </h3>
//               <button
//                 onClick={() => setShowAppointmentModal(false)}
//                 className="text-gray-400 hover:text-gray-500"
//               >
//                 <XIcon className="h-5 w-5" />
//               </button>
//             </div>

//             {messageSuccess ? (
//               <div className="flex-1 flex flex-col items-center justify-center p-8">
//                 <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
//                   <CheckCircle className="h-10 w-10 text-green-500" />
//                 </div>
//                 <p className="text-lg font-medium">Appointment scheduled!</p>
//                 <p className="text-gray-500 mt-1">
//                   Lead status updated to "Qualified"
//                 </p>
//               </div>
//             ) : appointmentStep === 1 ? (
//               <div className="flex-1 overflow-auto p-6">
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Appointment Date:
//                   </label>
//                   <input
//                     type="date"
//                     className="w-full p-2 border rounded"
//                     value={appointmentDate.toISOString().split("T")[0]}
//                     onChange={(e) =>
//                       setAppointmentDate(new Date(e.target.value))
//                     }
//                   />
//                 </div>

//                 <div className="mb-4">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Appointment Time:
//                   </label>
//                   <input
//                     type="time"
//                     className="w-full p-2 border rounded"
//                     value={appointmentTime}
//                     onChange={(e) => setAppointmentTime(e.target.value)}
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Notes:
//                   </label>
//                   <textarea
//                     rows={4}
//                     placeholder="Enter any additional information about the appointment"
//                     className="w-full p-2 border rounded"
//                     value={appointmentNotes}
//                     onChange={(e) => setAppointmentNotes(e.target.value)}
//                   ></textarea>
//                 </div>
//               </div>
//             ) : (
//               <div className="flex-1 overflow-auto p-6">
//                 <div className="bg-gray-50 p-3 rounded-md mb-4">
//                   <div className="flex items-center mb-1">
//                     <Calendar className="w-4 h-4 mr-1 text-purple-600" />
//                     <span>
//                       {appointmentDate.toLocaleDateString("en-US", {
//                         weekday: "long",
//                         month: "long",
//                         day: "numeric",
//                         year: "numeric",
//                       })}
//                     </span>
//                   </div>
//                   <div className="flex items-center">
//                     <Clock className="w-4 h-4 mr-1 text-purple-600" />
//                     <span>{appointmentTime}</span>
//                   </div>
//                 </div>

//                 <div className="mb-4">
//                   <label className="flex items-center space-x-2">
//                     <input
//                       type="radio"
//                       checked={selectedLead.email !== undefined}
//                       readOnly
//                       className={`h-4 w-4 ${!selectedLead.email ? "opacity-50" : ""
//                         }`}
//                     />
//                     <span
//                       className={`${!selectedLead.email ? "text-gray-400" : ""
//                         }`}
//                     >
//                       Send Email {!selectedLead.email && "(No email address)"}
//                     </span>
//                   </label>

//                   {selectedLead.email && (
//                     <div className="mt-2 ml-6">
//                       <textarea
//                         rows={8}
//                         className="w-full p-2 border rounded"
//                         value={appointmentEmailTemplate}
//                         onChange={(e) =>
//                           setAppointmentEmailTemplate(e.target.value)
//                         }
//                       ></textarea>
//                     </div>
//                   )}
//                 </div>

//                 <div>
//                   <label className="flex items-center space-x-2">
//                     <input
//                       type="radio"
//                       checked={selectedLead.phone !== undefined}
//                       readOnly
//                       className={`h-4 w-4 ${!selectedLead.phone ? "opacity-50" : ""
//                         }`}
//                     />
//                     <span
//                       className={`${!selectedLead.phone ? "text-gray-400" : ""
//                         }`}
//                     >
//                       Send SMS {!selectedLead.phone && "(No phone number)"}
//                     </span>
//                   </label>

//                   {selectedLead.phone && (
//                     <div className="mt-2 ml-6">
//                       <textarea
//                         rows={4}
//                         className="w-full p-2 border rounded"
//                         value={appointmentSmsTemplate}
//                         onChange={(e) =>
//                           setAppointmentSmsTemplate(e.target.value)
//                         }
//                       ></textarea>
//                       <p className="text-xs text-gray-500 mt-1">
//                         Characters: {appointmentSmsTemplate.length}/160
//                       </p>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}

//             <div className="p-6 border-t flex justify-end space-x-3">
//               {!messageSuccess && (
//                 <>
//                   <button
//                     onClick={() => {
//                       if (appointmentStep === 2) {
//                         setAppointmentStep(1);
//                       } else {
//                         setShowAppointmentModal(false);
//                       }
//                     }}
//                     className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
//                   >
//                     {appointmentStep === 2 ? "Back" : "Cancel"}
//                   </button>

//                   {appointmentStep === 1 ? (
//                     <button
//                       onClick={handleAppointmentNext}
//                       className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
//                     >
//                       Next
//                     </button>
//                   ) : (
//                     <>
//                       {selectedLead.email && (
//                         <button
//                           onClick={() => handleScheduleAppointment("email")}
//                           disabled={sendingMessage}
//                           className={`px-4 py-2 rounded-md text-white ${sendingMessage
//                             ? "bg-blue-400"
//                             : "bg-blue-600 hover:bg-blue-700"
//                             }`}
//                         >
//                           {sendingMessage ? "Sending..." : "Send Email"}
//                         </button>
//                       )}

//                       {selectedLead.phone && (
//                         <button
//                           onClick={() => handleScheduleAppointment("sms")}
//                           disabled={sendingMessage}
//                           className={`px-4 py-2 rounded-md text-white ${sendingMessage
//                             ? "bg-green-400"
//                             : "bg-green-600 hover:bg-green-700"
//                             }`}
//                         >
//                           {sendingMessage ? "Sending..." : "Send SMS"}
//                         </button>
//                       )}
//                     </>
//                   )}
//                 </>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Reminder Modal */}
//       {showReminderModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
//             <div className="flex justify-between items-center p-6 border-b">
//               <h3 className="text-lg font-medium">Send Appointment Reminder</h3>
//               <button
//                 onClick={() => setShowReminderModal(false)}
//                 className="text-gray-400 hover:text-gray-500"
//               >
//                 <XIcon className="h-5 w-5" />
//               </button>
//             </div>

//             <div className="flex-1 overflow-auto p-6">
//               <div className="bg-gray-50 p-3 rounded-md mb-4">
//                 <div className="flex items-center mb-1">
//                   <Calendar className="w-4 h-4 mr-1 text-purple-600" />
//                   <span>
//                     {new Date(selectedLead.appointment.date).toLocaleDateString(
//                       "en-US",
//                       {
//                         weekday: "long",
//                         month: "long",
//                         day: "numeric",
//                         year: "numeric",
//                       }
//                     )}
//                   </span>
//                 </div>
//                 <div className="flex items-center">
//                   <Clock className="w-4 h-4 mr-1 text-purple-600" />
//                   <span>{selectedLead.appointment.time}</span>
//                 </div>
//               </div>

//               <div className="space-y-3">
//                 <label className="flex items-center space-x-2">
//                   <input
//                     type="radio"
//                     checked={reminderType === "manual"}
//                     onChange={() => setReminderType("manual")}
//                     className="h-4 w-4"
//                   />
//                   <span>Send reminder now</span>
//                 </label>

//                 <label className="flex items-center space-x-2">
//                   <input
//                     type="radio"
//                     checked={reminderType === "schedule"}
//                     onChange={() => setReminderType("schedule")}
//                     className="h-4 w-4"
//                   />
//                   <span>Schedule automatic reminders</span>
//                 </label>

//                 {reminderType === "schedule" && (
//                   <div className="ml-6 bg-blue-50 p-3 rounded-md text-sm">
//                     <p>Reminders will be sent:</p>
//                     <ul className="list-disc list-inside mt-1 space-y-1">
//                       <li>3 days before appointment</li>
//                       <li>1 day before appointment</li>
//                       <li>1 hour before appointment</li>
//                     </ul>
//                   </div>
//                 )}
//               </div>
//             </div>

//             <div className="p-6 border-t flex justify-end space-x-3">
//               <button
//                 onClick={() => setShowReminderModal(false)}
//                 className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSetReminders}
//                 className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
//               >
//                 {reminderType === "manual"
//                   ? "Send Reminder"
//                   : "Schedule Reminders"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* SMS Modal */}
//       {showSmsModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
//             <div className="flex justify-between items-center p-6 border-b">
//               <h3 className="text-lg font-medium">
//                 Send SMS to {selectedLead.firstName} {selectedLead.lastName}
//               </h3>
//               <button
//                 onClick={() => setShowSmsModal(false)}
//                 className="text-gray-400 hover:text-gray-500"
//               >
//                 <XIcon className="h-5 w-5" />
//               </button>
//             </div>

//             {messageSuccess ? (
//               <div className="flex-1 flex flex-col items-center justify-center p-8">
//                 <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
//                   <CheckCircle className="h-10 w-10 text-green-500" />
//                 </div>
//                 <p className="text-lg font-medium">SMS sent successfully!</p>
//                 <p className="text-gray-500 mt-1">
//                   Lead status updated to "Contacted"
//                 </p>
//               </div>
//             ) : (
//               <div className="flex-1 overflow-auto p-6">
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     To:
//                   </label>
//                   <div className="p-2 bg-gray-100 rounded text-sm">
//                     {selectedLead.phone}
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Message:
//                   </label>
//                   <textarea
//                     rows={6}
//                     className="w-full p-2 border rounded"
//                     value={smsTemplate}
//                     onChange={(e) => setSmsTemplate(e.target.value)}
//                   ></textarea>
//                   <p className="text-xs text-gray-500 mt-1">
//                     Characters: {smsTemplate.length}/160
//                     {smsTemplate.length > 160 && (
//                       <span className="text-red-500">
//                         {" "}
//                         (Will be sent as multiple messages)
//                       </span>
//                     )}
//                   </p>
//                 </div>
//               </div>
//             )}

//             <div className="p-6 border-t flex justify-end space-x-3">
//               {!messageSuccess && (
//                 <>
//                   <button
//                     onClick={() => setShowSmsModal(false)}
//                     className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={handleSendSms}
//                     disabled={sendingMessage}
//                     className={`px-4 py-2 rounded-md text-white ${sendingMessage
//                       ? "bg-green-400"
//                       : "bg-green-600 hover:bg-green-700"
//                       }`}
//                   >
//                     {sendingMessage ? (
//                       <span className="flex items-center">
//                         <svg
//                           className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                           xmlns="http://www.w3.org/2000/svg"
//                           fill="none"
//                           viewBox="0 0 24 24"
//                         >
//                           <circle
//                             className="opacity-25"
//                             cx="12"
//                             cy="12"
//                             r="10"
//                             stroke="currentColor"
//                             strokeWidth="4"
//                           ></circle>
//                           <path
//                             className="opacity-75"
//                             fill="currentColor"
//                             d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                           ></path>
//                         </svg>
//                         Sending...
//                       </span>
//                     ) : (
//                       "Send SMS"
//                     )}
//                   </button>
//                 </>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Survey Modal */}
//       {showSurveyModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
//             <div className="flex justify-between items-center p-6 border-b">
//               <h3 className="text-lg font-medium">
//                 Lead Survey: {selectedLead.firstName} {selectedLead.lastName}
//               </h3>
//               <button
//                 onClick={() => setShowSurveyModal(false)}
//                 className="text-gray-400 hover:text-gray-500"
//               >
//                 <XIcon className="h-5 w-5" />
//               </button>
//             </div>

//             <div className="flex-1 overflow-auto p-6">
//               {/* Question manager */}
//               <div className="mb-6 pb-6 border-b">
//                 <h4 className="font-medium text-gray-900 mb-3">
//                   Manage Questions
//                 </h4>

//                 <div className="mb-4 flex">
//                   <input
//                     type="text"
//                     value={newQuestion}
//                     onChange={(e) => setNewQuestion(e.target.value)}
//                     placeholder="Add a new question..."
//                     className="flex-1 p-2 border rounded-l"
//                   />
//                   <button
//                     onClick={handleAddQuestion}
//                     className="px-4 py-2 bg-blue-600 text-white rounded-r hover:bg-blue-700"
//                   >
//                     Add
//                   </button>
//                 </div>

//                 {questions.length > 0 && (
//                   <div className="space-y-2 mb-4">
//                     {questions.map((question) => (
//                       <div
//                         key={question.id}
//                         className="flex items-center bg-gray-50 p-2 rounded"
//                       >
//                         {isEditingQuestion === question.id ? (
//                           <input
//                             type="text"
//                             value={question.text}
//                             onChange={(e) =>
//                               handleEditQuestion(question.id, e.target.value)
//                             }
//                             className="flex-1 p-1 border rounded mr-2"
//                             autoFocus
//                             onBlur={() => setIsEditingQuestion(null)}
//                             onKeyDown={(e) => {
//                               if (e.key === "Enter") setIsEditingQuestion(null);
//                             }}
//                           />
//                         ) : (
//                           <span className="flex-1">{question.text}</span>
//                         )}

//                         <div className="flex space-x-1">
//                           <button
//                             onClick={() => setIsEditingQuestion(question.id)}
//                             className="p-1 text-blue-600 hover:text-blue-800"
//                           >
//                             <Edit className="w-4 h-4" />
//                           </button>
//                           <button
//                             onClick={() => handleDeleteQuestion(question.id)}
//                             className="p-1 text-red-600 hover:text-red-800"
//                           >
//                             <Trash2 className="w-4 h-4" />
//                           </button>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>

//               {/* Survey form */}
//               <h4 className="font-medium text-gray-900 mb-3">
//                 Capture Responses
//               </h4>

//               {questions.length === 0 ? (
//                 <p className="text-gray-500 italic">
//                   No questions available. Add questions above.
//                 </p>
//               ) : (
//                 <div className="space-y-4">
//                   {questions.map((question) => (
//                     <div key={question.id} className="bg-gray-50 p-3 rounded">
//                       <label className="block font-medium text-gray-700 mb-2">
//                         {question.text}
//                       </label>
//                       <textarea
//                         rows={3}
//                         value={question.answer}
//                         onChange={(e) =>
//                           handleAnswerChange(question.id, e.target.value)
//                         }
//                         placeholder="Enter the customer's response..."
//                         className="w-full p-2 border rounded"
//                       ></textarea>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             <div className="p-6 border-t flex justify-end space-x-3">
//               <button
//                 onClick={() => setShowSurveyModal(false)}
//                 className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSaveSurvey}
//                 className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
//               >
//                 Save Survey
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default LeadManagement;
